import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import axios from "axios";
import { useParams } from "react-router-dom";
import { jsPDF } from "jspdf";
import baseURL from "../config";

function Prejoining() {
  const { id } = useParams();

  const [crew, setCrew] = useState(null);
  const [agentList, setAgentList] = useState([]);

  const [selectedCrew, setSelectedCrew] = useState("");
  const [selectedAgent, setSelectedAgent] = useState("");
  const [selectedForm, setSelectedForm] = useState("");

  const [loading, setLoading] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  // ✅ Fetch Crew & Agents
  useEffect(() => {
    const token = localStorage.getItem("token");

    axios
      .get(`${baseURL}/api/crewprofileById/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setCrew(res.data);
        setSelectedCrew(res.data._id);
      })
      .catch((err) => console.log("Crew Error:", err));

    axios
      .get(`${baseURL}/api/getAllSeaSeriviceProfiles`)
      .then((res) => {
        if (Array.isArray(res.data.data)) {
          setAgentList(res.data.data);
        } else {
          setAgentList([]);
        }
      })
      .catch((err) => console.log("Agent Error:", err));
  }, [id]);

  // ✅ PDF Generator (Frontend Only)
  const generateSeafarerDeclaration = async (crew, agentLogo, selectedAgentData) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;


    const marginLeft = 20;
    const marginRight = 20;
    const contentWidth = pageWidth - marginLeft - marginRight;
    const lineHeight = 6;

    let y = 20;

    // ==============================
    // HEADER SECTION - COMPANY INFO
    // ==============================

    // Logo (Top Left) - taken from selectedAgentData
    if (selectedAgentData?.logo?.filePath) {
      try {
        // Clean up the file path - replace backslashes with forward slashes
        const cleanFilePath = selectedAgentData.logo.filePath.replace(/\\/g, '/');

        const logoUrl = `${baseURL}/${cleanFilePath}`;

        // Determine image format from file extension
        const fileExtension = selectedAgentData.logo.filePath.split('.').pop().toLowerCase();
        let imageFormat = 'JPEG'; // Default format

        // Map common extensions to jsPDF supported formats
        const formatMap = {
          'jpg': 'JPEG',
          'jpeg': 'JPEG',
          'png': 'PNG',
          'gif': 'GIF',
          'bmp': 'BMP',
          'webp': 'WEBP'
        };

        imageFormat = formatMap[fileExtension] || 'JPEG';

        doc.addImage(logoUrl, imageFormat, marginLeft, 10, 35, 18);
      } catch (err) {
        console.log("Logo error:", err);
      }
    }

    // Company Information (Top Right)
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(
      selectedAgentData?.companyName || "SOULITE SHIP MANAGEMENT PVT LTD",
      pageWidth - marginRight,
      12,
      { align: "right" }
    );

    doc.setFontSize(9);
    doc.setFont(undefined, "normal");

    // Address
    const address = selectedAgentData?.address || "D-22, First Floor, Office No-03, Sector-03, Noida (UP) 201301";
    doc.text(address, pageWidth - marginRight, 18, {
      align: "right",
    });

    // Email
    doc.text(
      `Email: ${selectedAgentData?.email || "hr@souliteshipmanagement.com"}`,
      pageWidth - marginRight,
      24,
      { align: "right" }
    );

    // Mobile Number
    doc.text(
      `Tel: ${selectedAgentData?.mobileNumber || "+917303611301"}`,
      pageWidth - marginRight,
      30,
      { align: "right" }
    );

    // Divider Line
    doc.line(marginLeft, 38, pageWidth - marginRight, 38);

    y = 48;

    // ==============================
    // TITLE
    // ==============================
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text("SEAFARER DECLARATION", pageWidth / 2, y, {
      align: "center",
    });

    y += 12;

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");

    // ==============================
    // CREW DETAILS
    // ==============================

    const addField = (label, value) => {
      doc.setFont(undefined, "bold");
      doc.text(`${label}:`, marginLeft, y);
      doc.setFont(undefined, "normal");
      doc.text(`${value ?? "-"}`, marginLeft + 35, y);
      y += lineHeight;
    };

    addField("Name", crew?.name);
    addField("Rank", crew?.rankname);
    addField("Passport No", crew?.passport);
    addField("CDC No", crew?.cdc);
    addField("Indos No", crew?.indosNo ?? crew?.indosno);

    // Address (wrapped properly)
    doc.setFont(undefined, "bold");
    doc.text("Present Address:", marginLeft, y);
    doc.setFont(undefined, "normal");

    const addressLines = doc.splitTextToSize(
      crew?.presentAddress ?? "-",
      contentWidth - 35
    );

    doc.text(addressLines, marginLeft + 35, y);
    y += addressLines.length * lineHeight + 5;

    // ==============================
    // DECLARATION BODY
    // ==============================

    const declarationText = `
I hereby covenant with the company (${selectedAgentData?.companyName || "SOULITE SHIP MANAGEMENT PVT LTD"}) and declare that to the best of my knowledge and belief, I am not obligated under any contract including licenses, covenants, or commitments that would conflict with my obligation to promote the interest of the company.

Mr. ${crew?.name ?? "-"} is not going to bear any kind of charge directly or indirectly in whole or in part. All charges will be borne by the shipping company. As per Director General of Shipping Rule Section (I), 5, 2016, invention agreement, non-competition agreement or any other contract related to the relationship with the company or any previous employer.

I confirm that I am medically fit for the assignment and have made full disclosure of all material ailments.

I have understood the drug and alcohol policy of the principals and agree to abide by it. I will cooperate in routine and unannounced drug/alcohol tests carried out by owner's representatives or port state authorities.

I hereby accept the terms and conditions of engagement and declare this of my own free will without coercion.
`;

    const splitText = doc.splitTextToSize(declarationText, contentWidth);
    doc.text(splitText, marginLeft, y);
    y += splitText.length * lineHeight + 12;

    // ==============================
    // SIGNATURE SECTION
    // ==============================

    doc.line(marginLeft, y, marginLeft + 60, y);
    doc.line(pageWidth - marginRight - 60, y, pageWidth - marginRight, y);

    y += 6;

    doc.setFont(undefined, "bold");
    doc.text("Seafarer Signature", marginLeft, y);
    doc.text("Authorized Signatory", pageWidth - marginRight, y, {
      align: "right",
    });

    y += 15;

    doc.setFont(undefined, "normal");
    doc.text(`Place: ${crew?.presentAddress ?? "-"}`, marginLeft, y);

    doc.text(
      `Date: ${new Date().toLocaleDateString("en-GB").replace(/\//g, "-")}`,
      pageWidth - marginRight,
      y,
      { align: "right" }
    );
    // ==============================
    // FOOTER
    // ==============================

    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text(
      "This document is electronically generated and does not require a physical signature.",
      pageWidth / 2,
      pageHeight - 10,
      { align: "center" }
    );

    doc.save("Seafarer_Declaration.pdf");
  };

  const generateDrugAlcohalpolicy = async (crew, agentLogo, selectedAgentData) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const marginLeft = 20;
    const marginRight = 20;
    const contentWidth = pageWidth - marginLeft - marginRight;
    const lineHeight = 6;

    let y = 20;

    // ==============================
    // HEADER SECTION
    // ==============================

    // Logo (Top Left) - taken from selectedAgentData
    if (selectedAgentData?.logo?.filePath) {
      try {
        // Clean up the file path - replace backslashes with forward slashes
        const cleanFilePath = selectedAgentData.logo.filePath.replace(/\\/g, '/');

        const logoUrl = `${baseURL}/${cleanFilePath}`;

        // Determine image format from file extension
        const fileExtension = selectedAgentData.logo.filePath.split('.').pop().toLowerCase();
        let imageFormat = 'JPEG'; // Default format

        // Map common extensions to jsPDF supported formats
        const formatMap = {
          'jpg': 'JPEG',
          'jpeg': 'JPEG',
          'png': 'PNG',
          'gif': 'GIF',
          'bmp': 'BMP',
          'webp': 'WEBP'
        };

        imageFormat = formatMap[fileExtension] || 'JPEG';

        doc.addImage(logoUrl, imageFormat, marginLeft, 10, 35, 18);
      } catch (err) {
        console.log("Logo error:", err);
      }
    }

    // Company Information (Top Right)
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(
      selectedAgentData?.companyName || "SOULITE SHIP MANAGEMENT PVT LTD",
      pageWidth - marginRight,
      12,
      { align: "right" }
    );

    doc.setFontSize(9);
    doc.setFont(undefined, "normal");

    // Address
    const address = selectedAgentData?.address || "D-22, First Floor, Office No-03, Sector-03, Noida (UP) 201301";
    doc.text(address, pageWidth - marginRight, 18, {
      align: "right",
    });

    // Email
    doc.text(
      `Email: ${selectedAgentData?.email || "hr@souliteshipmanagement.com"}`,
      pageWidth - marginRight,
      24,
      { align: "right" }
    );

    // Mobile Number
    doc.text(
      `Tel: ${selectedAgentData?.mobileNumber || "+917303611301"}`,
      pageWidth - marginRight,
      30,
      { align: "right" }
    );

    // Divider Line
    doc.line(marginLeft, 38, pageWidth - marginRight, 38);

    y = 42;

    // ==============================
    // TITLE
    // ==============================
    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("DRUG & ALCOHOL POLICY CONFIRMATION", pageWidth / 2, y, {
      align: "center",
    });

    y += 15;

    doc.setFontSize(11);
    doc.setFont(undefined, "normal");

    // ==============================
    // POLICY CONTENT
    // ==============================

    const policyText = `It is hereby agreed between the contracting seaman and the OWNERS OF THE VESSEL through its agent, M/s. ${selectedAgentData?.companyName}`;

    const policyLines = doc.splitTextToSize(policyText, contentWidth);
    doc.text(policyLines, marginLeft, y);
    y += policyLines.length * lineHeight + 8;

    // Points with numbering
    doc.setFont(undefined, "normal");

    // Point 1
    const point1 = "1. Seaman manifests that he has not been convicted of any drug offenses, that he has not been a user of prohibited drugs, or does he intend to use such drugs for the duration of his employment onboard any vessel of the principal.";
    const point1Lines = doc.splitTextToSize(point1, contentWidth - 5);
    doc.text(point1Lines, marginLeft + 5, y);
    y += point1Lines.length * lineHeight + 5;

    // Point 2
    const point2 = "2. Seaman agrees that possession, use of, and/or trafficking of drugs will be considered as breach of contract and will result in his immediate termination. All expenses towards his repatriation and that of his reliever will be borne by him.";
    const point2Lines = doc.splitTextToSize(point2, contentWidth - 5);
    doc.text(point2Lines, marginLeft + 5, y);
    y += point2Lines.length * lineHeight + 5;

    // Point 3
    const point3 = "3. Seaman understands that any involvement with drugs is punishable by law of several Countries/States where the vessel is expected to visit which may range from fines to harsh jail terms to death penalty. The Company, Principals & Owners of the vessel will fully support any law enforcement agency of any State/Govt. in any investigation undertaken by them.";
    const point3Lines = doc.splitTextToSize(point3, contentWidth - 5);
    doc.text(point3Lines, marginLeft + 5, y);
    y += point3Lines.length * lineHeight + 5;

    // Point 4
    const point4 = "4. Seaman hereby voluntarily allows Owners/master's to conduct an alcohol or drug test without any warning onboard or at any place. Seaman's refusal to undergo such test would result in dismissal from services.";
    const point4Lines = doc.splitTextToSize(point4, contentWidth - 5);
    doc.text(point4Lines, marginLeft + 5, y);
    y += point4Lines.length * lineHeight + 5;

    // Point 5
    const point5 = "5. Seaman agrees to comply with the Company's/Owners DRUG AND ALCOHOL POLICY.";
    const point5Lines = doc.splitTextToSize(point5, contentWidth - 5);
    doc.text(point5Lines, marginLeft + 5, y);
    y += point5Lines.length * lineHeight + 12;

    // ==============================
    // AGREEMENT STATEMENT
    // ==============================
    doc.setFont(undefined, "bold");
    doc.text("I AGREE TO THE ABOVE", marginLeft, y);

    y += 15;

    // ==============================
    // SIGNATURE SECTION
    // ==============================
    doc.setFont(undefined, "normal");

    // Signature line
    doc.text("Signature of Seafarer: ______________________________", marginLeft, y);
    y += 10;

    // Name
    doc.text(`Name: ${crew?.name || "____________________"}`, marginLeft, y);
    y += 10;

    // Rank
    doc.text(`Rank: ${crew?.rankname || "____________________"}`, marginLeft, y);
    y += 10;

    // Vessel
    doc.text("Vessel: ________________________________", marginLeft, y);
    y += 10;

    // Date with dd-mm-yyyy format
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const formattedDate = `${day}-${month}-${year}`;

    doc.text(`Date: ${formattedDate}`, marginLeft, y);

    y += 15;
    // ==============================
    // COMPANY NAME AT BOTTOM
    // ==============================
    const companyName =
      selectedAgentData?.companyName;

    doc.setFont(undefined, "bold");
    doc.text(companyName, pageWidth / 2, pageHeight - 20, {
      align: "center",
    });

    // ==============================
    // FOOTER
    // ==============================
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.setFont(undefined, "normal");
    doc.text(
      "This document is electronically generated and does not require a physical signature.",
      pageWidth / 2,
      pageHeight - 10,
      { align: "center" }
    );

    doc.save("Drug_Alcohol_Policy.pdf");
  };

  const generateMarpolCompliance = async (crew, agentLogo, selectedAgentData) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const marginLeft = 20;
    const marginRight = 20;
    const contentWidth = pageWidth - marginLeft - marginRight;
    const lineHeight = 6;

    let y = 20;

    // ==============================
    // HEADER SECTION
    // ==============================

    // Logo (Top Left) - taken from selectedAgentData
    if (selectedAgentData?.logo?.filePath) {
      try {
        // Clean up the file path - replace backslashes with forward slashes
        const cleanFilePath = selectedAgentData.logo.filePath.replace(/\\/g, '/');

        const logoUrl = `${baseURL}/${cleanFilePath}`;

        // Determine image format from file extension
        const fileExtension = selectedAgentData.logo.filePath.split('.').pop().toLowerCase();
        let imageFormat = 'JPEG'; // Default format

        // Map common extensions to jsPDF supported formats
        const formatMap = {
          'jpg': 'JPEG',
          'jpeg': 'JPEG',
          'png': 'PNG',
          'gif': 'GIF',
          'bmp': 'BMP',
          'webp': 'WEBP'
        };

        imageFormat = formatMap[fileExtension] || 'JPEG';

        doc.addImage(logoUrl, imageFormat, marginLeft, 10, 35, 18);
      } catch (err) {
        console.log("Logo error:", err);
      }
    }

    // Company Information (Top Right)
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(
      selectedAgentData?.companyName || "SOULITE SHIP MANAGEMENT PVT LTD",
      pageWidth - marginRight,
      12,
      { align: "right" }
    );

    doc.setFontSize(9);
    doc.setFont(undefined, "normal");

    // Address
    const address = selectedAgentData?.address || "D-22, First Floor, Office No-03, Sector-03, Noida (UP) 201301";
    doc.text(address, pageWidth - marginRight, 18, {
      align: "right",
    });

    // Email
    doc.text(
      `Email: ${selectedAgentData?.email || "hr@souliteshipmanagement.com"}`,
      pageWidth - marginRight,
      24,
      { align: "right" }
    );

    // Mobile Number
    doc.text(
      `Tel: ${selectedAgentData?.mobileNumber || "+917303611301"}`,
      pageWidth - marginRight,
      30,
      { align: "right" }
    );

    // Divider Line
    doc.line(marginLeft, 38, pageWidth - marginRight, 38);

    y = 42;

    // ==============================
    // TITLE
    // ==============================
    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("MARPOL COMPLIANCE", pageWidth / 2, y, {
      align: "center",
    });

    y += 15;

    doc.setFontSize(11);
    doc.setFont(undefined, "normal");

    // ==============================
    // POLICY CONTENT
    // ==============================

    const introText = `We, ${selectedAgentData?.companyName}, place the very highest importance on:`;

    const introLines = doc.splitTextToSize(introText, contentWidth);
    doc.text(introLines, marginLeft, y);
    y += introLines.length * lineHeight + 4;

    // Points with numbering
    doc.setFont(undefined, "normal");

    // Point 1
    const point1 = "1. Full and strict compliance with the MARPOL regulations and company policies stated in its Safety Management System and circulars.";
    const point1Lines = doc.splitTextToSize(point1, contentWidth - 5);
    doc.text(point1Lines, marginLeft + 5, y);
    y += point1Lines.length * lineHeight;

    // Point 2
    const point2 = "2. On the proper disposal of all oily wastes, including bilge slops and sludge, by the proper use of the equipment on board the vessel or by sending them ashore to approved facilities equipped to deal with them properly.";
    const point2Lines = doc.splitTextToSize(point2, contentWidth - 5);
    doc.text(point2Lines, marginLeft + 5, y);
    y += point2Lines.length * lineHeight;

    // Point 3
    const point3 = "3. The full and correct completion of all records, including those relevant to MARPOL and SMS, such as the Oil Record Book and the sounding book.";
    const point3Lines = doc.splitTextToSize(point3, contentWidth - 5);
    doc.text(point3Lines, marginLeft + 5, y);
    y += point3Lines.length * lineHeight;

    // Warning Text (Bold and Uppercase)
    doc.setFont(undefined, "bold");
    const warningText = "NON-COMPLIANCE WITH THE ABOVE INSTRUCTIONS MAY MAKE THE COMPANY LIABLE FOR ILLEGAL BREACHES OF MARPOL AND YOU WILL BE AT RISK OF GOING THROUGH PENAL PROCEDURES WHICH COULD RESULT IN YOUR IMPRISONMENT.";
    const warningLines = doc.splitTextToSize(warningText, contentWidth);
    doc.text(warningLines, marginLeft, y);
    y += warningLines.length * lineHeight + 2;

    // Regular text continues
    doc.setFont(undefined, "normal");

    const para1 = "Therefore, it is very important that you follow the above instructions and correctly and closely follow the MARPOL rules and the company's policies and procedures, both for the company's protection and for your own employment and protection. If you become aware of any problem that may lead to or allow a violation of MARPOL or these company requirements, you are instructed to immediately inform the Captain or the Chief Engineer.";
    const para1Lines = doc.splitTextToSize(para1, contentWidth);
    doc.text(para1Lines, marginLeft, y);
    y += para1Lines.length * lineHeight + 1;

    const para2 = "If you do not feel comfortable reporting your concerns to the captain or to the Chief Engineer, you are strongly urged to call owner/ our office in confidence using the telephone number displayed on board the vessel to inform the Designated Person or the Deputy Designated Person so that we can intervene and correct the situation.";
    const para2Lines = doc.splitTextToSize(para2, contentWidth);
    doc.text(para2Lines, marginLeft, y);
    y += para2Lines.length * lineHeight;

    const para3 = "We ask you to personally acknowledge that you have read, understand and will follow this instruction by signing below.";
    const para3Lines = doc.splitTextToSize(para3, contentWidth);
    doc.text(para3Lines, marginLeft, y);
    y += para3Lines.length * lineHeight + 2;

    // ==============================
    // CONFIRMATION STATEMENT
    // ==============================
    doc.setFont(undefined, "bold");
    const confirmText = "I CONFIRM THAT I HAVE READ, UNDERSTOOD AND WILL FOLLOW THIS INSTRUCTION THROUGH OUT THE PERIOD OF MY EMPLOYMENT:";
    const confirmLines = doc.splitTextToSize(confirmText, contentWidth);
    doc.text(confirmLines, marginLeft, y);

    y += confirmLines.length * lineHeight + 4;

    // ==============================
    // SIGNATURE SECTION
    // ==============================
    doc.setFont(undefined, "normal");

    // Signature line
    doc.text("Signature of Seafarer: ______________________________", marginLeft, y);
    y += 10;

    // Name
    doc.text(`Name: ${crew?.name || "____________________"}`, marginLeft, y);
    y += 10;

    // Rank
    doc.text(`Rank: ${crew?.rankname || "____________________"}`, marginLeft, y);
    y += 10;

    // Vessel
    doc.text("Vessel: ________________________________", marginLeft, y);
    y += 10;

    // Date with dd-mm-yyyy format
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const formattedDate = `${day}-${month}-${year}`;

    doc.text(`Date: ${formattedDate}`, marginLeft, y);

    y += 15;

    // ==============================
    // COMPANY NAME AT BOTTOM
    // ==============================
    doc.setFont(undefined, "bold");
    doc.text(selectedAgentData?.companyName, pageWidth / 2, pageHeight - 20, {
      align: "center",
    });

    // ==============================
    // FOOTER
    // ==============================
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.setFont(undefined, "normal");
    doc.text(
      "This document is electronically generated and does not require a physical signature.",
      pageWidth / 2,
      pageHeight - 10,
      { align: "center" }
    );

    doc.save("Marpol_Compliance.pdf");
  };

  const generateNextofKinDeclaration = async (crew, agentLogo, selectedAgentData) => {
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const marginLeft = 15;
    const marginRight = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;
    const lineHeight = 6;

    let y = 20;

    // ==============================
    // HEADER SECTION
    // ==============================

    // Logo (Top Left) - taken from selectedAgentData
    if (selectedAgentData?.logo?.filePath) {
      try {
        // Clean up the file path - replace backslashes with forward slashes
        const cleanFilePath = selectedAgentData.logo.filePath.replace(/\\/g, '/');

        const logoUrl = `${baseURL}/${cleanFilePath}`;

        // Determine image format from file extension
        const fileExtension = selectedAgentData.logo.filePath.split('.').pop().toLowerCase();
        let imageFormat = 'JPEG'; // Default format

        // Map common extensions to jsPDF supported formats
        const formatMap = {
          'jpg': 'JPEG',
          'jpeg': 'JPEG',
          'png': 'PNG',
          'gif': 'GIF',
          'bmp': 'BMP',
          'webp': 'WEBP'
        };

        imageFormat = formatMap[fileExtension] || 'JPEG';

        doc.addImage(logoUrl, imageFormat, marginLeft, 10, 35, 18);
      } catch (err) {
        console.log("Logo error:", err);
      }
    }

    // Company Information (Top Right)
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(
      selectedAgentData?.companyName || "SOULITE SHIP MANAGEMENT PVT LTD",
      pageWidth - marginRight,
      12,
      { align: "right" }
    );

    doc.setFontSize(9);
    doc.setFont(undefined, "normal");

    // Address
    const address = selectedAgentData?.address || "D-22, First Floor, Office No-03, Sector-03, Noida (UP) 201301";
    doc.text(address, pageWidth - marginRight, 18, {
      align: "right",
    });

    // Email
    doc.text(
      `Email: ${selectedAgentData?.email || "hr@souliteshipmanagement.com"}`,
      pageWidth - marginRight,
      24,
      { align: "right" }
    );

    // Mobile Number
    doc.text(
      `Tel: ${selectedAgentData?.mobileNumber || "+917303611301"}`,
      pageWidth - marginRight,
      30,
      { align: "right" }
    );

    // Divider Line
    doc.line(marginLeft, 38, pageWidth - marginRight, 38);

    y = 42;

    // ==============================
    // TITLE
    // ==============================
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text("NEXT OF KIN DECLARATION", pageWidth / 2, y, {
      align: "center",
    });

    y += 12;

    doc.setFontSize(10);
    doc.setFont(undefined, "normal");

    // ==============================
    // SEAFARER DETAILS
    // ==============================

    // Helper function to add detail row
    const addDetailRow = (label, value) => {
      doc.setFont(undefined, "bold");
      doc.text(label, marginLeft, y);
      doc.setFont(undefined, "normal");
      doc.text(value, marginLeft + 45, y);
      y += lineHeight + 1;
    };

    addDetailRow("NAME OF SEAFARER:", crew?.name || "_______________________");
    addDetailRow("RANK:", crew?.rankname || "_______________________");
    addDetailRow("VESSEL JOINING:", "_______________________");
    addDetailRow("PASSPORT NO.:", crew?.passport || "_______________________");
    addDetailRow("CDC NO.:", crew?.cdc || "_______________________");
    addDetailRow("INDOS NO.:", crew?.indosNo || crew?.indosno || "_______________________");

    y += 2;

    // ==============================
    // DECLARATION TEXT
    // ==============================
    const declarationText = "In the event of my death during tenure on the above-mentioned vessel, compensation payable if any should be paid to the following persons as per the percentages indicated below. Balance of Wages and personal effects should be given to my next of kin whose name is appeared at serial number 1.";

    const declarationLines = doc.splitTextToSize(declarationText, contentWidth);
    doc.setFontSize(9);
    doc.text(declarationLines, marginLeft, y);
    y += declarationLines.length * lineHeight + 2;

    // ==============================
    // NEXT OF KIN TABLE HEADER
    // ==============================

    // Reset font for table
    doc.setFontSize(9);
    doc.setFont(undefined, "bold");

    // Calculate column positions
    const col1 = marginLeft;                          // Sr. No.
    const col2 = marginLeft + 20;                      // Name
    const col3 = marginLeft + 80;                      // Address, Mobile No
    const col4 = marginLeft + 120;                     // Relation
    const col5 = marginLeft + 150;                     // Percentage

    doc.text("Sr. No.", col1, y);
    doc.text("Name", col2, y);
    doc.text("Address, Mobile No", col3, y);
    doc.text("Relation", col4, y);
    doc.text("Percentage", col5, y);

    y += lineHeight + 1;

    // Draw table lines
    doc.setFont(undefined, "normal");

    // Check if NOK data exists and display it
    const nokData = crew?.NOK || [];

    if (nokData.length > 0) {
      // Display actual NOK data
      for (let i = 0; i < Math.min(nokData.length, 4); i++) {
        const nok = nokData[i];

        // Row number
        doc.text((i + 1).toString(), col1, y);

        // Name
        doc.text(nok.name || "-", col2, y);

        // Address and Mobile - combine if available
        let addressMobile = "";
        if (nok.bankdetails && nok.bankdetails.length > 0) {
          const bankDetail = nok.bankdetails[0];
          addressMobile = bankDetail.address || "";
        }
        addressMobile = addressMobile + (nok.mobile ? `, ${nok.mobile}` : "");
        doc.text(addressMobile.substring(0, 25) || "-", col3, y); // Truncate if too long

        // Relation
        doc.text(nok.relation || "-", col4, y);

        // Percentage
        doc.text(nok.percentage || "-", col5, y);

        y += lineHeight + 3;
      }

      // Fill remaining rows with underscores if less than 4 entries
      for (let i = nokData.length; i < 4; i++) {
        doc.text((i + 1).toString(), col1, y);
        doc.text("________________", col2, y);
        doc.text("_______________", col3, y);
        doc.text("_________", col4, y);
        doc.text("___", col5, y);
        y += lineHeight + 3;
      }
    } else {
      // No NOK data - show empty rows with underscores
      for (let i = 1; i <= 4; i++) {
        doc.text(i.toString(), col1, y);
        doc.text("________________", col2, y);
        doc.text("_______________", col3, y);
        doc.text("_________", col4, y);
        doc.text("___", col5, y);
        y += lineHeight + 3;
      }
    }

    y += 2;

    // ==============================
    // SPOUSE AND CHILDREN SECTION
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(9);
    doc.text("Information Regarding Spouse and Children:", marginLeft, y);
    y += lineHeight + 2;

    // Sub-table headers
    doc.setFontSize(9);
    const familyCol1 = marginLeft;                    // Name
    const familyCol2 = marginLeft + 75;                // Relation
    const familyCol3 = marginLeft + 125;               // Date of Birth

    doc.text("Name", familyCol1, y);
    doc.text("Relation", familyCol2, y);
    doc.text("Date of Birth", familyCol3, y);

    y += lineHeight + 1;

    // Draw 3 empty rows for family members
    doc.setFont(undefined, "normal");
    for (let i = 1; i <= 3; i++) {
      doc.text("____________", familyCol1, y);
      doc.text("____________", familyCol2, y);
      doc.text("____________", familyCol3, y);
      y += lineHeight + 3;
    }

    y += 4;

    // ==============================
    // SIGNATURE SECTION
    // ==============================

    // Check if we need a new page
    if (y > pageHeight - 50) {
      doc.addPage();
      y = 20;
    }

    doc.setFontSize(9);
    // Signature of seafarer
    doc.text("Signature of seafarer: ______________________________", marginLeft, y);
    y += 8;

    // Date
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const formattedDate = `${day}-${month}-${year}`;

    doc.text(`Date: ${formattedDate}`, marginLeft, y);
    y += 8;

    // Place - use presentAddress from crew data
    const place = crew?.presentAddress || "______________";
    doc.text(`Place: ${place}`, marginLeft, y);
    y += 12;

    // ==============================
    // COMPANY NAME AT BOTTOM
    // ==============================

    doc.setFont(undefined, "bold");
    doc.setFontSize(8);

    doc.text(
      `${selectedAgentData?.companyName}`,
      pageWidth / 2,
      pageHeight - 15,
      { align: "center" }
    );

    // ==============================
    // FOOTER
    // ==============================
    doc.setFontSize(7);
    doc.setTextColor(100);
    doc.setFont(undefined, "normal");
    doc.text(
      "This document is electronically generated and does not require a physical signature.",
      pageWidth / 2,
      pageHeight - 8,
      { align: "center" }
    );

    doc.save("Next_of_Kin_Declaration.pdf");
  };

  const generateSeafarerAwarenessform = async (crew, agentLogo, selectedAgentData) => {

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const marginLeft = 15;
    const marginRight = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;
    const lineHeight = 6;

    let y = 20;

    // ==============================
    // HEADER SECTION
    // ==============================

    // Logo (Top Left) - taken from selectedAgentData
    if (selectedAgentData?.logo?.filePath) {
      try {
        // Clean up the file path - replace backslashes with forward slashes
        const cleanFilePath = selectedAgentData.logo.filePath.replace(/\\/g, '/');

        const logoUrl = `${baseURL}/${cleanFilePath}`;

        // Determine image format from file extension
        const fileExtension = selectedAgentData.logo.filePath.split('.').pop().toLowerCase();
        let imageFormat = 'JPEG'; // Default format

        // Map common extensions to jsPDF supported formats
        const formatMap = {
          'jpg': 'JPEG',
          'jpeg': 'JPEG',
          'png': 'PNG',
          'gif': 'GIF',
          'bmp': 'BMP',
          'webp': 'WEBP'
        };

        imageFormat = formatMap[fileExtension] || 'JPEG';

        doc.addImage(logoUrl, imageFormat, marginLeft, 10, 35, 18);
      } catch (err) {
        console.log("Logo error:", err);
      }
    }

    // Company Information (Top Right)
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(
      selectedAgentData?.companyName || "SOULITE SHIP MANAGEMENT PVT LTD",
      pageWidth - marginRight,
      12,
      { align: "right" }
    );

    doc.setFontSize(9);
    doc.setFont(undefined, "normal");

    // Address
    const address = selectedAgentData?.address || "D-22, First Floor, Office No-03, Sector-03, Noida (UP) 201301";
    doc.text(address, pageWidth - marginRight, 18, {
      align: "right",
    });

    // Email
    doc.text(
      `Email: ${selectedAgentData?.email || "hr@souliteshipmanagement.com"}`,
      pageWidth - marginRight,
      24,
      { align: "right" }
    );

    // Mobile Number
    doc.text(
      `Tel: ${selectedAgentData?.mobileNumber || "+917303611301"}`,
      pageWidth - marginRight,
      30,
      { align: "right" }
    );

    // Divider Line
    doc.line(marginLeft, 38, pageWidth - marginRight, 38);

    y = 42;

    // ==============================
    // TITLE
    // ==============================
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text("SEAFARER AWARENESS FORM", pageWidth / 2, y, {
      align: "center",
    });

    y += 12;

    // ==============================
    // SEAFARER DETAILS ROW
    // ==============================
    doc.setFontSize(9);
    doc.setFont(undefined, "normal");

    // Row 1: Rank and CDC No.
    doc.setFont(undefined, "bold");
    doc.text("Rank:", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text(crew?.rankname || "______________", marginLeft + 20, y);

    doc.setFont(undefined, "bold");
    doc.text("CDC No.:", marginLeft + 80, y);
    doc.setFont(undefined, "normal");
    doc.text(crew?.cdc || "______________", marginLeft + 115, y);

    y += lineHeight + 2;

    // Row 2: Seafarer's Name and Indos No.
    doc.setFont(undefined, "bold");
    doc.text("Seafarer's Name:", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text(crew?.name || "______________", marginLeft + 45, y);

    doc.setFont(undefined, "bold");
    doc.text("Indos No.:", marginLeft + 120, y);
    doc.setFont(undefined, "normal");
    doc.text(crew?.indosno || crew?.indosNo || "______________", marginLeft + 155, y);

    y += lineHeight + 2;

    // Row 3: Vessel Name (below Indos No.)
    doc.setFont(undefined, "bold");
    doc.text("Vessel Name:", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text("_______________________", marginLeft + 45, y);

    y += lineHeight + 6;

    // ==============================
    // TOPIC TABLE HEADER
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(9);

    // Draw table borders
    doc.rect(marginLeft, y - 4, contentWidth, 8); // Header cell background

    const col1 = marginLeft + 2;           // S.N (10px)
    const col2 = marginLeft + 20;           // TOPIC (120px)
    const col3 = marginLeft + 140;           // Yes (20px)
    const col4 = marginLeft + 165;           // No (20px)

    doc.text("S.N", col1, y);
    doc.text("TOPIC", col2, y);
    doc.text("Yes", col3, y);
    doc.text("No", col4, y);

    y += lineHeight + 1;

    // Draw horizontal line under header
    doc.line(marginLeft, y - 2, pageWidth - marginRight, y - 2);

    doc.setFont(undefined, "normal");

    // ==============================
    // TOPIC ROWS
    // ==============================
    const topics = [
      "I have been informed of my rights and duties under the employment agreements prior to or in the process of engagement",

      "I have been advised of any particular conditions applicable to the job for which I am being engaged and of the particular ship-owners policies relating to the employment and also advised on possible problems of Signing on a ship that flies the flag of a state which has not ratified the MLC, 2006.",

      "I have examined my employment agreement before to seek advice before signing, and after it has been signed, and I have received a signed original of",

      "I have been explained and familiarized with on-board complaint Procedures, as applicable on the ship.",

      `I haven't been subject to exploitation by ${selectedAgentData?.companyName || "Shreysun Global Shipping Pvt.Ltd"} or by their personnel, with regard to the offer of engagement on particular ships or by particular companies`,

      `I haven't been subject to exploitation by ${selectedAgentData?.companyName || "Shreysun Global Shipping Pvt.Ltd"} or by their personnel, with regard to the offer of joining advances or any other financial transaction between the ship owner and me`,

      `I observed that ${selectedAgentData?.companyName || "Shreysun Global Shipping Pvt.Ltd"} staff was polite to me.`,

      `I have read & understood Seafarer Recruitment Policy of ${selectedAgentData?.companyName || "Shreysun Global Shipping Pvt.Ltd"}.`,

      "All the certificates Submitted by me for employment are genuine and Checked by me"
    ];

    for (let i = 0; i < topics.length; i++) {
      // Check if we need a new page
      if (y > pageHeight - 60) {
        doc.addPage();
        y = 20;

        // Redraw header on new page
        doc.setFont(undefined, "bold");
        doc.setFontSize(9);
        doc.rect(marginLeft, y - 4, contentWidth, 8);
        doc.text("S.N", col1, y);
        doc.text("TOPIC", col2, y);
        doc.text("Yes", col3, y);
        doc.text("No", col4, y);
        y += lineHeight + 1;
        doc.line(marginLeft, y - 2, pageWidth - marginRight, y - 2);
        doc.setFont(undefined, "normal");
      }

      // Draw row border
      const rowStartY = y - 4;

      // Topic number
      doc.text((i + 1).toString(), col1, y);

      // Topic text - wrap long text
      const topicLines = doc.splitTextToSize(topics[i], col3 - col2 - 15);
      doc.text(topicLines, col2, y);

      // Calculate row height based on number of lines
      const rowHeight = (topicLines.length * lineHeight) + 4;

      // Yes and No columns - keep blank as requested
      doc.text("", col3, y);
      doc.text("", col4, y);

      // Draw vertical lines for table
      doc.line(col2 - 5, rowStartY, col2 - 5, rowStartY + rowHeight); // After S.N
      doc.line(col3 - 5, rowStartY, col3 - 5, rowStartY + rowHeight); // After Topic
      doc.line(col4 - 5, rowStartY, col4 - 5, rowStartY + rowHeight); // After Yes
      doc.line(pageWidth - marginRight, rowStartY, pageWidth - marginRight, rowStartY + rowHeight); // Right border

      // Draw bottom border for the row
      doc.line(marginLeft, rowStartY + rowHeight, pageWidth - marginRight, rowStartY + rowHeight);

      y += rowHeight;
    }

    y += 4;

    // ==============================
    // GRIEVANCE PROCEDURE POINTS
    // ==============================
    const grievancePoints = [
      "1. In case the seafarer employed through the company have any grievance or complaints they can approach the Master of the vessel.",
      "2. Master of the vessel will inform the company, owner or Manager and try his level best to address/ resolve the grievance.",
      "3. However, if the grievance is not sorted out then Master of the vessel should consult the Ship Owners / Clients or our Director who shall try its level best to address the grievance of the seafarer.",
      "4. Moreover, if the grievance is not sorted out by any of the above the seafarer has the right to approach the Director General of Shipping or Seamen's Employment Office of the relevant flag authority.",
      "5. The company will co-operate fully with the seafarer and the authorities to resolve the grievance of",
      "6. Seafarer has the right to refer complaints against the Company directly to the Director- General of Shipping or the suitable authority of the country to which the ship belongs."
    ];

    doc.setFontSize(8.5);
    doc.setFont(undefined, "normal");

    for (let point of grievancePoints) {
      // Check if we need a new page
      if (y > pageHeight - 40) {
        doc.addPage();
        y = 20;
      }

      const pointLines = doc.splitTextToSize(point, contentWidth - 5);
      doc.text(pointLines, marginLeft, y);
      y += (pointLines.length * lineHeight) + 1;
    }

    y += 2;

    // ==============================
    // EMERGENCY CONTACT
    // ==============================
    doc.setFont(undefined, "bold");
    doc.text("Our 24 hrs (emergency) contact number is as follows:", marginLeft, y);
    y += lineHeight + 1;

    doc.setFont(undefined, "normal");
    doc.text("a) Mr. Pradeep Kumar: +91-8009733276", marginLeft + 5, y);

    y += lineHeight + 4;

    // ==============================
    // SIGNATURE SECTION
    // ==============================

    // Check if we need a new page
    if (y > pageHeight - 30) {
      doc.addPage();
      y = 20;
    }

    doc.setFont(undefined, "bold");
    doc.text("Signature of Seafarer:", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text("______________________________", marginLeft + 55, y);

    y += lineHeight + 2;

    // Date
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    const formattedDate = `${day}-${month}-${year}`;

    doc.setFont(undefined, "bold");
    doc.text("Date:", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text(formattedDate, marginLeft + 20, y);

    y += lineHeight + 6;

    // ==============================
    // COMPANY NAME AT BOTTOM
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(8);

    doc.text(
      `${selectedAgentData?.companyName}`,
      pageWidth / 2,
      pageHeight - 15,
      { align: "center" }
    );

    // ==============================
    // FOOTER
    // ==============================
    doc.setFontSize(7);
    doc.setTextColor(100);
    doc.setFont(undefined, "normal");
    doc.text(
      "This document is electronically generated and does not require a physical signature.",
      pageWidth / 2,
      pageHeight - 8,
      { align: "center" }
    );

    doc.save("Seafarer_Awareness_Form.pdf");
  };

  const generateAcknowledgementofapplication = async (crew, agentLogo, selectedAgentData) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const marginLeft = 15;
    const marginRight = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;
    const lineHeight = 6;

    let y = 20;

    // ==============================
    // HEADER SECTION
    // ==============================

    // Logo (Top Left) - taken from selectedAgentData
    if (selectedAgentData?.logo?.filePath) {
      try {
        // Clean up the file path - replace backslashes with forward slashes
        const cleanFilePath = selectedAgentData.logo.filePath.replace(/\\/g, '/');

        const logoUrl = `${baseURL}/${cleanFilePath}`;

        // Determine image format from file extension
        const fileExtension = selectedAgentData.logo.filePath.split('.').pop().toLowerCase();
        let imageFormat = 'JPEG'; // Default format

        // Map common extensions to jsPDF supported formats
        const formatMap = {
          'jpg': 'JPEG',
          'jpeg': 'JPEG',
          'png': 'PNG',
          'gif': 'GIF',
          'bmp': 'BMP',
          'webp': 'WEBP'
        };

        imageFormat = formatMap[fileExtension] || 'JPEG';

        doc.addImage(logoUrl, imageFormat, marginLeft, 10, 35, 18);
      } catch (err) {
        console.log("Logo error:", err);
      }
    }

    // Company Information (Top Right)
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(
      selectedAgentData?.companyName || "SOULITE SHIP MANAGEMENT PVT LTD",
      pageWidth - marginRight,
      12,
      { align: "right" }
    );

    doc.setFontSize(9);
    doc.setFont(undefined, "normal");

    // Address
    const address = selectedAgentData?.address || "D-22, First Floor, Office No-03, Sector-03, Noida (UP) 201301";
    doc.text(address, pageWidth - marginRight, 18, {
      align: "right",
    });

    // Email
    doc.text(
      `Email: ${selectedAgentData?.email || "hr@souliteshipmanagement.com"}`,
      pageWidth - marginRight,
      24,
      { align: "right" }
    );

    // Mobile Number
    doc.text(
      `Tel: ${selectedAgentData?.mobileNumber || "+917303611301"}`,
      pageWidth - marginRight,
      30,
      { align: "right" }
    );

    // Divider Line
    doc.line(marginLeft, 38, pageWidth - marginRight, 38);

    y = 42;

    // ==============================
    // TITLE
    // ==============================
    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text("ACKNOWLEDGEMENT OF APPLICATION", pageWidth / 2, y, {
      align: "center",
    });

    y += 6;

    doc.setFontSize(12);
    doc.text("FOR RECRUITMENT OR PLACEMENT", pageWidth / 2, y, {
      align: "center",
    });

    y += 12;

    // ==============================
    // SEAFARER DETAILS
    // ==============================
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");

    // Name of Seafarer
    doc.setFont(undefined, "bold");
    doc.text("Name of Seafarer:", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text(crew?.name || "_______________________", marginLeft + 45, y);
    y += lineHeight + 2;

    // Rank applied for
    doc.setFont(undefined, "bold");
    doc.text("Rank applied for:", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text(crew?.rankname || "_______________________", marginLeft + 45, y);
    y += lineHeight + 2;

    // Passport number
    doc.setFont(undefined, "bold");
    doc.text("Passport number:", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text(crew?.passport || "_______________________", marginLeft + 45, y);
    y += lineHeight + 2;

    // CDC number / COC number
    doc.setFont(undefined, "bold");
    doc.text("CDC number / COC number:", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text(crew?.cdc || "_______________________", marginLeft + 60, y);

    y += lineHeight + 6;

    // ==============================
    // SALUTATION
    // ==============================
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.text("Dear Sir / Madam,", marginLeft, y);

    y += lineHeight + 4;

    // ==============================
    // MAIN CONTENT
    // ==============================
    const content1 = "With regard to your application in this company for Recruitment and Placement on board the vessel. We confirm safe receipt of same. We shall further sort, process your data provided by you.";
    const contentLines1 = doc.splitTextToSize(content1, contentWidth);
    doc.text(contentLines1, marginLeft, y);
    y += (contentLines1.length * lineHeight) + 3;

    const content2 = "This acknowledgement is not a confirmation of your employment. In any case your employment is subject to an interview, medical examination, verification of your documents and final approval of Management.";
    const contentLines2 = doc.splitTextToSize(content2, contentWidth);
    doc.text(contentLines2, marginLeft, y);
    y += (contentLines2.length * lineHeight) + 3;

    const content3 = "We assure you that the details and records provided by you, including but not limited to: -";
    doc.text(content3, marginLeft, y);
    y += lineHeight + 2;

    // Bullet points
    doc.text("1. Your qualification.", marginLeft + 5, y);
    y += lineHeight + 1;
    doc.text("2. Your record of employment.", marginLeft + 5, y);
    y += lineHeight + 1;
    doc.text("3. Personal data relevant to employment.", marginLeft + 5, y);
    y += lineHeight + 1;
    doc.text("4. Medical data relevant to employment.", marginLeft + 5, y);
    y += lineHeight + 2;

    const content4 = "Will be kept confidential and will be used by us for the purpose of your recruitment and placement. The acknowledgement for Recruitment and Placement issued by our office does not absolve you of your rights of seeking employment with other company.";
    const contentLines4 = doc.splitTextToSize(content4, contentWidth);
    doc.text(contentLines4, marginLeft, y);
    y += (contentLines4.length * lineHeight) + 6;

    // ==============================
    // SIGNATURE SECTION
    // ==============================

    // Check if we need a new page
    if (y > pageHeight - 60) {
      doc.addPage();
      y = 20;
    }

    // Signature of Seafarer (Left side)
    doc.setFont(undefined, "bold");
    doc.text("SIGNATURE OF SEAFARER", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text("___________________________", marginLeft + 55, y);

    doc.setFont(undefined, "italic");
    doc.setFontSize(8);
    doc.text("(ACK. RECEIVED)", marginLeft + 55, y + 4);

    y += lineHeight + 12; // Increased spacing to separate left and right sections

    // Company signature section (Right side)
    const rightX = pageWidth - marginRight; // This positions from the right margin

    // Line - right aligned
    doc.text("_________________________________________________", rightX, y, { align: "right" });
    y += lineHeight + 2;

    // Company name - right aligned
    doc.setFont(undefined, "bold");
    doc.setFontSize(9);

    const companyName =
      selectedAgentData?.companyName;

    doc.text(`For, ${companyName}.`, rightX, y, { align: "right" });

    y += lineHeight + 1;

    // Authorized Signatory - right aligned
    doc.setFont(undefined, "normal");
    doc.setFontSize(8);
    doc.text("(Authorized Signatory)", rightX, y, { align: "right" });

    y += lineHeight + 8; // Increased spacing before NOTE section
    // ==============================
    // NOTE
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(8);
    doc.text("NOTE:", marginLeft, y);

    doc.setFont(undefined, "normal");
    const noteText = "A COPY OF ACKNOWLEDGEMENT SIGNED BY THE SEAFARER OR A PROOF OF SENDING SUCH ACKNOWLEDGEMENT ELECTRONICALLY TO BE KEPT IN OFFICE RECORD";
    const noteLines = doc.splitTextToSize(noteText, contentWidth - 15);
    doc.text(noteLines, marginLeft + 12, y);

    y += (noteLines.length * lineHeight) + 8;

    // ==============================
    // COMPANY NAME AT BOTTOM
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(8);

    doc.text(
      `${selectedAgentData?.companyName}`,
      pageWidth / 2,
      pageHeight - 15,
      { align: "center" }
    );

    // ==============================
    // FOOTER
    // ==============================
    doc.setFontSize(7);
    doc.setTextColor(100);
    doc.setFont(undefined, "normal");
    doc.text(
      "This document is electronically generated and does not require a physical signature.",
      pageWidth / 2,
      pageHeight - 8,
      { align: "center" }
    );

    doc.save("Acknowledgement_of_Application.pdf");
  };

  const generateApplicationFormone = async (crew, agentLogo, selectedAgentData) => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });


    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const marginLeft = 15;
    const marginRight = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;
    const lineHeight = 6;

    let y = 20;
    let pageNumber = 1;

    // ==============================
    // HEADER SECTION
    // ==============================

    // Logo (Top Left) - taken from selectedAgentData
    if (selectedAgentData?.logo?.filePath) {
      try {
        // Clean up the file path - replace backslashes with forward slashes
        const cleanFilePath = selectedAgentData.logo.filePath.replace(/\\/g, '/');

        const logoUrl = `${baseURL}/${cleanFilePath}`;

        // Determine image format from file extension
        const fileExtension = selectedAgentData.logo.filePath.split('.').pop().toLowerCase();
        let imageFormat = 'JPEG'; // Default format

        // Map common extensions to jsPDF supported formats
        const formatMap = {
          'jpg': 'JPEG',
          'jpeg': 'JPEG',
          'png': 'PNG',
          'gif': 'GIF',
          'bmp': 'BMP',
          'webp': 'WEBP'
        };

        imageFormat = formatMap[fileExtension] || 'JPEG';

        doc.addImage(logoUrl, imageFormat, marginLeft, 10, 35, 18);
      } catch (err) {
        console.log("Logo error:", err);
      }
    }

    // Company Information (Top Right)
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(
      selectedAgentData?.companyName || "SOULITE SHIP MANAGEMENT PVT LTD",
      pageWidth - marginRight,
      12,
      { align: "right" }
    );

    doc.setFontSize(9);
    doc.setFont(undefined, "normal");

    // Address
    const address = selectedAgentData?.address || "D-22, First Floor, Office No-03, Sector-03, Noida (UP) 201301";
    doc.text(address, pageWidth - marginRight, 18, {
      align: "right",
    });

    // Email
    doc.text(
      `Email: ${selectedAgentData?.email || "hr@souliteshipmanagement.com"}`,
      pageWidth - marginRight,
      24,
      { align: "right" }
    );

    // Mobile Number
    doc.text(
      `Tel: ${selectedAgentData?.mobileNumber || "+917303611301"}`,
      pageWidth - marginRight,
      30,
      { align: "right" }
    );

    // Divider Line
    doc.line(marginLeft, 38, pageWidth - marginRight, 38);

    y = 48;

    // ==============================
    // TITLE
    // ==============================
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("APPLICATION FORM / PERSONAL DATA UPDATE FORM", pageWidth / 2, y, {
      align: "center",
    });

    y += lineHeight + 6;

    // ==============================
    // TOP SECTION - TABLE FORMAT
    // ==============================
    doc.setFontSize(9);

    // Create top table (2x2 grid)
    const topTableY = y;
    const colWidth = contentWidth / 2;

    // Draw table borders
    doc.rect(marginLeft, topTableY - 4, contentWidth, 30);

    // Draw vertical line
    doc.line(marginLeft + colWidth, topTableY - 4, marginLeft + colWidth, topTableY + 26);

    // Draw horizontal line
    doc.line(marginLeft, topTableY + 11, pageWidth - marginRight, topTableY + 11);

    // Row 1, Col 1: PRESENT RANK
    doc.setFont(undefined, "bold");
    doc.text("PRESENT RANK", marginLeft + 5, topTableY);
    doc.setFont(undefined, "normal");
    doc.text(crew?.rankname || "______________", marginLeft + 5, topTableY + 6);

    // Row 1, Col 2: DATE OF APPLICATION
    doc.setFont(undefined, "bold");
    doc.text("DATE OF APPLICATION", marginLeft + colWidth + 5, topTableY);
    doc.setFont(undefined, "normal");
    const today = new Date();
    const formattedDate = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
    doc.text(formattedDate, marginLeft + colWidth + 5, topTableY + 6);

    // Row 2, Col 1: RANK APPLIED
    doc.setFont(undefined, "bold");
    doc.text("RANK APPLIED", marginLeft + 5, topTableY + 16);
    doc.setFont(undefined, "normal");
    doc.text(crew?.rankname || "______________", marginLeft + 5, topTableY + 22);

    // Row 2, Col 2: DATE OF AVAILABILITY
    doc.setFont(undefined, "bold");
    doc.text("DATE OF AVAILABILITY", marginLeft + colWidth + 5, topTableY + 16);
    doc.setFont(undefined, "normal");
    doc.text("______________", marginLeft + colWidth + 5, topTableY + 22);

    y = topTableY + 35;

    // ==============================
    // SECTION 1: PERSONAL DETAILS
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text("1. PERSONAL DETAILS", marginLeft, y);
    y += lineHeight + 2;

    doc.setFontSize(9);

    // Personal Details Table - 4 Column Layout
    const personalTableY = y;
    const personalTableHeight = 125;

    // Calculate column widths - 4 columns
    const col1Width = 35; // For labels
    const col2Width = 50; // For first value
    const col3Width = 45; // For second value
    const col4Width = contentWidth - (col1Width + col2Width + col3Width); // Remaining space

    // Draw outer border
    doc.rect(marginLeft, personalTableY - 4, contentWidth, personalTableHeight);

    // Draw vertical lines for 4 columns
    doc.line(marginLeft + col1Width, personalTableY - 4, marginLeft + col1Width, personalTableY + personalTableHeight - 4);
    doc.line(marginLeft + col1Width + col2Width, personalTableY - 4, marginLeft + col1Width + col2Width, personalTableY + personalTableHeight - 4);
    doc.line(marginLeft + col1Width + col2Width + col3Width, personalTableY - 4, marginLeft + col1Width + col2Width + col3Width, personalTableY + personalTableHeight - 4);

    // Draw horizontal lines for each row
    const rowPositions = [
      personalTableY + 10,  // Row 1: NAME row
      personalTableY + 24,  // Row 2: Date of Birth row
      personalTableY + 38,  // Row 3: Permanent Address row
      personalTableY + 52,  // Row 4: Present Address row
      personalTableY + 66,  // Row 5: City & Pin Code row
      personalTableY + 80,  // Row 6: Telephone row
      personalTableY + 94,  // Row 7: Email row
      personalTableY + 108  // Row 8: Airport row
    ];

    rowPositions.forEach(pos => {
      doc.line(marginLeft, pos, pageWidth - marginRight, pos);
    });

    // Row 1: NAME | FIRST NAME | MIDDLE NAME | SURNAME
    doc.setFont(undefined, "bold");
    doc.text("NAME", marginLeft + 5, personalTableY);
    doc.text("FIRST NAME", marginLeft + col1Width + 5, personalTableY);
    doc.text("MIDDLE NAME", marginLeft + col1Width + col2Width + 5, personalTableY);
    doc.text("SURNAME", marginLeft + col1Width + col2Width + col3Width + 5, personalTableY);

    doc.setFont(undefined, "normal");
    const nameParts = crew?.name ? crew.name.split(' ') : [];
    const firstName = nameParts[0] || "______________";
    const middleName = nameParts.length > 2 ? nameParts.slice(1, -1).join(' ') : "______________";
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "______________";

    doc.text(firstName, marginLeft + col1Width + 5, personalTableY + 6);
    doc.text(middleName, marginLeft + col1Width + col2Width + 5, personalTableY + 6);
    doc.text(lastName, marginLeft + col1Width + col2Width + col3Width + 5, personalTableY + 6);

    // Row 2: Date of Birth | Place of Birth | (empty) | (empty)
    doc.setFont(undefined, "bold");
    doc.text("Date of Birth", marginLeft + 5, personalTableY + 14);
    doc.text("Place of Birth", marginLeft + col1Width + 5, personalTableY + 14);

    doc.setFont(undefined, "normal");
    const dob = crew?.dateofBirth ? new Date(crew.dateofBirth).toLocaleDateString() : "______________";
    doc.text(dob, marginLeft + 5, personalTableY + 20);
    doc.text(crew?.placOfBirth || "______________", marginLeft + col1Width + 5, personalTableY + 20);

    // Row 3: Permanent Address: | (value spans 3 columns)
    doc.setFont(undefined, "bold");
    doc.text("Permanent Address:", marginLeft + 5, personalTableY + 28);
    doc.setFont(undefined, "normal");
    doc.text("__________________________________________________", marginLeft + col1Width + 5, personalTableY + 34);

    // Row 4: Present Address: | (value spans 3 columns)
    doc.setFont(undefined, "bold");
    doc.text("Present Address:", marginLeft + 5, personalTableY + 42);
    doc.setFont(undefined, "normal");
    doc.text(crew?.presentAddress?.substring(0, 40) || "________________________________________", marginLeft + col1Width + 5, personalTableY + 48);

    // Row 5: City & Pin Code: | (value) | (empty) | (empty)
    doc.setFont(undefined, "bold");
    doc.text("City & Pin Code:", marginLeft + 5, personalTableY + 56);
    doc.setFont(undefined, "normal");
    doc.text("______________", marginLeft + col1Width + 5, personalTableY + 62);

    // Row 6: Telephone: | (value) | (empty) | (empty)
    doc.setFont(undefined, "bold");
    doc.text("Telephone:", marginLeft + 5, personalTableY + 70);
    doc.setFont(undefined, "normal");
    doc.text(crew?.mobile || "______________", marginLeft + col1Width + 5, personalTableY + 76);

    // Row 7: E-mail 1: | (value) | E-mail 2: | (value)
    doc.setFont(undefined, "bold");
    doc.text("E-mail 1:", marginLeft + 5, personalTableY + 84);
    doc.setFont(undefined, "normal");
    doc.text(crew?.email || "______________", marginLeft + 5, personalTableY + 90);

    doc.setFont(undefined, "bold");
    doc.text("E-mail 2:", marginLeft + col1Width + col2Width + 5, personalTableY + 84);
    doc.setFont(undefined, "normal");
    doc.text("______________", marginLeft + col1Width + col2Width + 5, personalTableY + 90);

    // Row 8: Nearest Domestic Airport: | (value) | Nearest International Airport: | (value)
    doc.setFont(undefined, "bold");
    doc.text("Nearest Domestic Airport:", marginLeft + 5, personalTableY + 98);
    doc.setFont(undefined, "normal");
    doc.text("______________", marginLeft + 5, personalTableY + 104);

    doc.setFont(undefined, "bold");
    doc.text("Nearest International Airport:", marginLeft + col1Width + col2Width + 5, personalTableY + 98);
    doc.setFont(undefined, "normal");
    doc.text("______________", marginLeft + col1Width + col2Width + 5, personalTableY + 104);

    y = personalTableY + personalTableHeight + 8;

    // ==============================
    // BANK DETAILS
    // ==============================
    const bankTableY = y;
    const bankTableHeight = 45;

    // Get bank details from NOK data
    const bankData = crew?.NOK && crew.NOK.length > 0 && crew.NOK[0].bankdetails && crew.NOK[0].bankdetails.length > 0
      ? crew.NOK[0].bankdetails[0]
      : null;

    doc.setFont(undefined, "bold");
    doc.text("Bank Details:", marginLeft, bankTableY - 4);

    doc.rect(marginLeft, bankTableY - 2, contentWidth, bankTableHeight);

    // Draw vertical lines for 4 columns
    doc.line(marginLeft + col1Width, bankTableY - 2, marginLeft + col1Width, bankTableY + bankTableHeight - 2);
    doc.line(marginLeft + col1Width + col2Width, bankTableY - 2, marginLeft + col1Width + col2Width, bankTableY + bankTableHeight - 2);
    doc.line(marginLeft + col1Width + col2Width + col3Width, bankTableY - 2, marginLeft + col1Width + col2Width + col3Width, bankTableY + bankTableHeight - 2);

    // Draw horizontal lines
    doc.line(marginLeft, bankTableY + 10, pageWidth - marginRight, bankTableY + 10);
    doc.line(marginLeft, bankTableY + 24, pageWidth - marginRight, bankTableY + 24);

    // Row 1: Bank Details: | Name & Branch: | (empty) | (empty)
    doc.setFont(undefined, "bold");
    doc.text("Bank Details:", marginLeft + 5, bankTableY);
    doc.text("Name & Branch:", marginLeft + col1Width + 5, bankTableY);
    doc.setFont(undefined, "normal");
    const bankNameBranch = bankData ? `${bankData.bankName || ""}${bankData.branch ? " - " + bankData.branch : ""}` : "";
    doc.text(bankNameBranch || "_________________________", marginLeft + col1Width + 5, bankTableY + 6);

    // Row 2: Account No: | (value) | TYPE-SAVING / CURRENT | (checkboxes)
    doc.setFont(undefined, "bold");
    doc.text("Account No:", marginLeft + 5, bankTableY + 16);
    doc.setFont(undefined, "normal");
    doc.text(bankData?.accountNo ? bankData.accountNo.toString() : "_________________", marginLeft + 5, bankTableY + 22);

    doc.setFont(undefined, "bold");
    doc.text("TYPE-SAVING / CURRENT", marginLeft + col1Width + col2Width + 5, bankTableY + 16);

    // Add checkboxes for SAVING and CURRENT
    doc.setFont(undefined, "normal");
    doc.text("SAVING", marginLeft + col1Width + col2Width + 5, bankTableY + 24);
    doc.rect(marginLeft + col1Width + col2Width + 20, bankTableY + 20, 3, 3); // Checkbox for SAVING
    doc.text("CURRENT", marginLeft + col1Width + col2Width + 30, bankTableY + 24);
    doc.rect(marginLeft + col1Width + col2Width + 50, bankTableY + 20, 3, 3); // Checkbox for CURRENT

    // Row 3: RTGS/IFSC: | (value) | (empty) | (empty)
    doc.setFont(undefined, "bold");
    doc.text("RTGS/IFSC:", marginLeft + 5, bankTableY + 32);
    doc.setFont(undefined, "normal");
    doc.text(bankData?.ifsc || "_________________", marginLeft + 5, bankTableY + 38);

    y = bankTableY + bankTableHeight + 8;

    // ==============================
    // CHECK IF NEED NEW PAGE FOR SECTION 2
    // ==============================
    if (y > pageHeight - 80) {
      doc.addPage();
      pageNumber++;
      y = 20;

      // Add header on new page
      doc.setFontSize(8);
      doc.setFont(undefined, "normal");
      doc.text(`Page ${pageNumber}`, pageWidth - marginRight, 15, { align: "right" });
      doc.line(marginLeft, 18, pageWidth - marginRight, 18);
    }

    // ==============================
    // SECTION 2: NEXT OF KIN / FAMILY PARTICULARS
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text("2. NEXT OF KIN / FAMILY PARTICULARS", marginLeft, y);
    y += lineHeight + 4;

    doc.setFontSize(9);

    // Get NOK data
    const nokData = crew?.NOK && crew.NOK.length > 0 ? crew.NOK[0] : null;

    // NAME and RELATION row
    doc.setFont(undefined, "bold");
    doc.text("NAME:", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text(nokData?.name || ":- ______________________________", marginLeft + 20, y);

    doc.setFont(undefined, "bold");
    doc.text("RELATION:", marginLeft + 100, y);
    doc.setFont(undefined, "normal");
    doc.text(nokData?.relation || ":- ______________________________", marginLeft + 130, y);

    y += lineHeight + 4;

    // ADDRESS row
    doc.setFont(undefined, "bold");
    doc.text("ADDRESS: Permanent / Present / other (please write):", marginLeft, y);
    y += lineHeight + 2;

    doc.setFont(undefined, "normal");
    let addressText = nokData?.bankdetails && nokData.bankdetails.length > 0 ? nokData.bankdetails[0].address || "" : "";
    doc.text(addressText, marginLeft, y);
    y += lineHeight + 2;
    doc.text("_________________________________________________________________________________", marginLeft, y);
    y += lineHeight + 6;

    // ==============================
    // FAMILY MEMBERS TABLE
    // ==============================
    const familyTableY = y;
    const familyTableHeight = 70; // Height for 4 rows

    // Draw table border
    doc.rect(marginLeft, familyTableY - 4, contentWidth, familyTableHeight);

    // Define column widths
    const famCol1 = marginLeft + 45;      // NAME column
    const famCol2 = marginLeft + 95;      // D.O.B. column
    const famCol3 = marginLeft + 135;     // PP/NO. column
    const famCol4 = marginLeft + 175;     // ISSUED / VALIDITY column

    // Draw vertical lines
    doc.line(famCol1, familyTableY - 4, famCol1, familyTableY + familyTableHeight - 4);
    doc.line(famCol2, familyTableY - 4, famCol2, familyTableY + familyTableHeight - 4);
    doc.line(famCol3, familyTableY - 4, famCol3, familyTableY + familyTableHeight - 4);
    doc.line(famCol4, familyTableY - 4, famCol4, familyTableY + familyTableHeight - 4);

    // Draw horizontal lines for rows
    doc.line(marginLeft, familyTableY + 10, pageWidth - marginRight, familyTableY + 10);
    doc.line(marginLeft, familyTableY + 24, pageWidth - marginRight, familyTableY + 24);
    doc.line(marginLeft, familyTableY + 38, pageWidth - marginRight, familyTableY + 38);
    doc.line(marginLeft, familyTableY + 52, pageWidth - marginRight, familyTableY + 52);

    // Table Headers
    doc.setFont(undefined, "bold");
    doc.text("NAME", marginLeft + 5, familyTableY);
    doc.text("D.O.B.", famCol1 + 5, familyTableY);
    doc.text("PP/NO.", famCol2 + 5, familyTableY);
    doc.text("ISSUED / VALIDITY", famCol3 + 5, familyTableY);

    // Row 1: WIFE
    doc.setFont(undefined, "bold");
    doc.text("WIFE", marginLeft + 5, familyTableY + 16);
    doc.setFont(undefined, "normal");
    doc.text("______________", famCol1 + 5, familyTableY + 16);
    doc.text("______________", famCol2 + 5, familyTableY + 16);
    doc.text("______________", famCol3 + 5, familyTableY + 16);
    doc.text("______________", famCol4 + 5, familyTableY + 16);

    // Row 2: CHILD (M/F)
    doc.setFont(undefined, "bold");
    doc.text("CHILD (M/F)", marginLeft + 5, familyTableY + 30);
    doc.setFont(undefined, "normal");
    doc.text("______________", famCol1 + 5, familyTableY + 30);
    doc.text("______________", famCol2 + 5, familyTableY + 30);
    doc.text("______________", famCol3 + 5, familyTableY + 30);
    doc.text("______________", famCol4 + 5, familyTableY + 30);

    // Row 3: CHILD (M/F)
    doc.setFont(undefined, "bold");
    doc.text("CHILD (M/F)", marginLeft + 5, familyTableY + 44);
    doc.setFont(undefined, "normal");
    doc.text("______________", famCol1 + 5, familyTableY + 44);
    doc.text("______________", famCol2 + 5, familyTableY + 44);
    doc.text("______________", famCol3 + 5, familyTableY + 44);
    doc.text("______________", famCol4 + 5, familyTableY + 44);

    // Row 4: CHILD (M/F)
    doc.setFont(undefined, "bold");
    doc.text("CHILD (M/F)", marginLeft + 5, familyTableY + 58);
    doc.setFont(undefined, "normal");
    doc.text("______________", famCol1 + 5, familyTableY + 58);
    doc.text("______________", famCol2 + 5, familyTableY + 58);
    doc.text("______________", famCol3 + 5, familyTableY + 58);
    doc.text("______________", famCol4 + 5, familyTableY + 58);

    y = familyTableY + familyTableHeight + 8;

    // ==============================
    // ADDITIONAL SECTION - EMERGENCY CONTACT
    // ==============================

    // Check if we need a new page
    if (y > pageHeight - 50) {
      doc.addPage();
      pageNumber++;
      y = 20;

      // Add header on new page
      doc.setFontSize(8);
      doc.setFont(undefined, "normal");
      doc.text(`Page ${pageNumber}`, pageWidth - marginRight, 15, { align: "right" });
      doc.line(marginLeft, 18, pageWidth - marginRight, 18);
    }


    // ==============================
    // FOOTER
    // ==============================
    doc.setFontSize(7);
    doc.setTextColor(100);
    doc.setFont(undefined, "normal");
    doc.text(
      "This document is electronically generated and does not require a physical signature.",
      pageWidth / 2,
      pageHeight - 8,
      { align: "center" }
    );

    doc.save("Application_Form_Complete.pdf");
  };

  const generateApplicationFormTwo = async (crew, agentLogo, selectedAgentData) => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });


    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const marginLeft = 15;
    const marginRight = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;
    const lineHeight = 6;

    let y = 20;
    let pageNumber = 1;

    // ==============================
    // HEADER SECTION
    // ==============================

    // Logo (Top Left) - taken from selectedAgentData
    if (selectedAgentData?.logo?.filePath) {
      try {
        // Clean up the file path - replace backslashes with forward slashes
        const cleanFilePath = selectedAgentData.logo.filePath.replace(/\\/g, '/');

        const logoUrl = `${baseURL}/${cleanFilePath}`;

        // Determine image format from file extension
        const fileExtension = selectedAgentData.logo.filePath.split('.').pop().toLowerCase();
        let imageFormat = 'JPEG'; // Default format

        // Map common extensions to jsPDF supported formats
        const formatMap = {
          'jpg': 'JPEG',
          'jpeg': 'JPEG',
          'png': 'PNG',
          'gif': 'GIF',
          'bmp': 'BMP',
          'webp': 'WEBP'
        };

        imageFormat = formatMap[fileExtension] || 'JPEG';

        doc.addImage(logoUrl, imageFormat, marginLeft, 10, 35, 18);
      } catch (err) {
        console.log("Logo error:", err);
      }
    }
    // Company Information (Top Right)
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(
      selectedAgentData?.companyName || "SOULITE SHIP MANAGEMENT PVT LTD",
      pageWidth - marginRight,
      12,
      { align: "right" }
    );

    doc.setFontSize(9);
    doc.setFont(undefined, "normal");

    // Address
    const address = selectedAgentData?.address || "D-22, First Floor, Office No-03, Sector-03, Noida (UP) 201301";
    doc.text(address, pageWidth - marginRight, 18, {
      align: "right",
    });

    // Email
    doc.text(
      `Email: ${selectedAgentData?.email || "hr@souliteshipmanagement.com"}`,
      pageWidth - marginRight,
      24,
      { align: "right" }
    );

    // Mobile Number
    doc.text(
      `Tel: ${selectedAgentData?.mobileNumber || "+917303611301"}`,
      pageWidth - marginRight,
      30,
      { align: "right" }
    );

    // Divider Line
    doc.line(marginLeft, 38, pageWidth - marginRight, 38);

    y = 48;
    // ==============================
    // TITLE
    // ==============================
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("APPLICATION FORM / PERSONAL DATA UPDATE FORM (PAGE 2)", pageWidth / 2, y, {
      align: "center",
    });

    y += lineHeight + 10;

    // ==============================
    // SECTION 3: PASSPORT DETAILS
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text("3. PASSPORT DETAILS", marginLeft, y);
    y += lineHeight + 4;

    doc.setFontSize(8);

    // Passport Details Table
    const passportTableY = y;
    const passportTableHeight = 50; // Height for 5 rows

    // Define column widths
    const col1Width = 35; // Label column
    const col2Width = 38; // NUMBER column
    const col3Width = 38; // PLACE OF ISSUE column
    const col4Width = 35; // DATE OF ISSUE column
    const col5Width = 35; // DATE OF EXPIRY column
    const totalColWidth = col1Width + col2Width + col3Width + col4Width + col5Width;

    // Adjust to fit content width
    const scaleFactor = contentWidth / totalColWidth;
    const scaledCol1 = col1Width * scaleFactor;
    const scaledCol2 = col2Width * scaleFactor;
    const scaledCol3 = col3Width * scaleFactor;
    const scaledCol4 = col4Width * scaleFactor;
    const scaledCol5 = col5Width * scaleFactor;

    // Draw table border
    doc.rect(marginLeft, passportTableY - 4, contentWidth, passportTableHeight);

    // Draw vertical lines
    let currentX = marginLeft + scaledCol1;
    doc.line(currentX, passportTableY - 4, currentX, passportTableY + passportTableHeight - 4);

    currentX += scaledCol2;
    doc.line(currentX, passportTableY - 4, currentX, passportTableY + passportTableHeight - 4);

    currentX += scaledCol3;
    doc.line(currentX, passportTableY - 4, currentX, passportTableY + passportTableHeight - 4);

    currentX += scaledCol4;
    doc.line(currentX, passportTableY - 4, currentX, passportTableY + passportTableHeight - 4);

    // Draw horizontal lines for each row
    const passportRowPositions = [
      passportTableY,        // Header row
      passportTableY + 10,   // PASSPORT row
      passportTableY + 20,   // VISA row
      passportTableY + 30,   // ECNR row
      passportTableY + 40,   // YELLOW FEVER row
      passportTableY + 50    // INDOS row (extra for last row)
    ];

    passportRowPositions.forEach((pos, index) => {
      if (index > 0 && index < passportRowPositions.length) {
        doc.line(marginLeft, pos - 4, pageWidth - marginRight, pos - 4);
      }
    });

    // Table Headers
    doc.setFont(undefined, "bold");
    doc.text("", marginLeft + 5, passportTableY); // Empty cell
    doc.text("NUMBER", marginLeft + scaledCol1 + 5, passportTableY);
    doc.text("PLACE OF", marginLeft + scaledCol1 + scaledCol2 + 5, passportTableY);
    doc.text("DATE OF", marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + 5, passportTableY);
    doc.text("DATE OF", marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + scaledCol4 + 5, passportTableY);

    doc.text("ISSUE", marginLeft + scaledCol1 + scaledCol2 + 5, passportTableY + 4);
    doc.text("ISSUE", marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + 5, passportTableY + 4);
    doc.text("EXPIRY", marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + scaledCol4 + 5, passportTableY + 4);

    // Row 1: PASSPORT
    doc.setFont(undefined, "bold");
    doc.text("PASSPORT", marginLeft + 5, passportTableY + 8);
    doc.setFont(undefined, "normal");
    // Increased vertical offset from +8 to +9 to avoid border line
    doc.text(crew?.passport || "______________", marginLeft + scaledCol1 + 5, passportTableY + 9);
    doc.text(crew?.passportPlaceOfIssue || "______________", marginLeft + scaledCol1 + scaledCol2 + 5, passportTableY + 9);

    // Format passport issue date if available
    const passportIssueDate = crew?.passportIssueDate ? new Date(crew.passportIssueDate).toLocaleDateString() : "______________";
    const passportExpiryDate = crew?.passportExpiryDate ? new Date(crew.passportExpiryDate).toLocaleDateString() : "______________";

    // Increased vertical offset from +8 to +9
    doc.text(passportIssueDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + 5, passportTableY + 9);
    doc.text(passportExpiryDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + scaledCol4 + 5, passportTableY + 9);

    // Row 2: VISA
    doc.setFont(undefined, "bold");
    doc.text("VISA", marginLeft + 5, passportTableY + 18);
    doc.setFont(undefined, "normal");
    // Increased vertical offset from +18 to +19
    doc.text(crew?.visaNumber || "______________", marginLeft + scaledCol1 + 5, passportTableY + 19);
    doc.text(crew?.visaPlaceOfIssue || "______________", marginLeft + scaledCol1 + scaledCol2 + 5, passportTableY + 19);

    const visaIssueDate = crew?.visaIssueDate ? new Date(crew.visaIssueDate).toLocaleDateString() : "______________";
    const visaExpiryDate = crew?.visaExpiryDate ? new Date(crew.visaExpiryDate).toLocaleDateString() : "______________";

    doc.text(visaIssueDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + 5, passportTableY + 19);
    doc.text(visaExpiryDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + scaledCol4 + 5, passportTableY + 19);

    // Row 3: ECNR
    doc.setFont(undefined, "bold");
    doc.text("ECNR", marginLeft + 5, passportTableY + 28);
    doc.setFont(undefined, "normal");
    // Increased vertical offset from +28 to +29
    doc.text(crew?.ecnr || "______________", marginLeft + scaledCol1 + 5, passportTableY + 29);
    doc.text(crew?.ecnrPlaceOfIssue || "______________", marginLeft + scaledCol1 + scaledCol2 + 5, passportTableY + 29);

    const ecnrIssueDate = crew?.ecnrIssueDate ? new Date(crew.ecnrIssueDate).toLocaleDateString() : "______________";
    const ecnrExpiryDate = crew?.ecnrExpiryDate ? new Date(crew.ecnrExpiryDate).toLocaleDateString() : "______________";

    doc.text(ecnrIssueDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + 5, passportTableY + 29);
    doc.text(ecnrExpiryDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + scaledCol4 + 5, passportTableY + 29);

    // Row 4: YELLOW FEVER
    doc.setFont(undefined, "bold");
    doc.text("YELLOW", marginLeft + 5, passportTableY + 36);
    doc.text("FEVER", marginLeft + 5, passportTableY + 40);
    doc.setFont(undefined, "normal");

    const yellowFeverDate = crew?.yellowFeverDate ? new Date(crew.yellowFeverDate).toLocaleDateString() : "______________";
    const yellowFeverExpiry = crew?.yellowFeverExpiry ? new Date(crew.yellowFeverExpiry).toLocaleDateString() : "______________";

    // Increased vertical offsets from +38 to +39
    doc.text(yellowFeverDate, marginLeft + scaledCol1 + 5, passportTableY + 39);
    doc.text(crew?.yellowFeverPlaceOfIssue || "______________", marginLeft + scaledCol1 + scaledCol2 + 5, passportTableY + 39);
    doc.text(yellowFeverDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + 5, passportTableY + 39);
    doc.text(yellowFeverExpiry, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + scaledCol4 + 5, passportTableY + 39);

    // Row 5: INDOS
    doc.setFont(undefined, "bold");
    doc.text("INDOS", marginLeft + 5, passportTableY + 48);
    doc.setFont(undefined, "normal");
    // Increased vertical offset from +48 to +49
    doc.text(crew?.indosno || "______________", marginLeft + scaledCol1 + 5, passportTableY + 49);
    doc.text(crew?.indosPlaceOfIssue || "______________", marginLeft + scaledCol1 + scaledCol2 + 5, passportTableY + 49);

    const indosIssueDate = crew?.indosIssueDate ? new Date(crew.indosIssueDate).toLocaleDateString() : "______________";
    const indosExpiryDate = crew?.indosExpiryDate ? new Date(crew.indosExpiryDate).toLocaleDateString() : "______________";

    doc.text(indosIssueDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + 5, passportTableY + 49);
    doc.text(indosExpiryDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + scaledCol4 + 5, passportTableY + 49);

    y = passportTableY + passportTableHeight + 8;

    // ==============================
    // SECTION 4: CDC DETAILS
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text("4. CONTINUOUS DISCHARGE CERTIFICATE (S)", marginLeft, y);
    y += lineHeight + 2;

    doc.setFontSize(8);

    // CDC Table
    const cdcTableY = y;
    const cdcTableHeight = 50; // Height for 5 rows

    // Draw table border
    doc.rect(marginLeft, cdcTableY - 4, contentWidth, cdcTableHeight);

    // Draw vertical lines (same column widths as passport table)
    currentX = marginLeft + scaledCol1;
    doc.line(currentX, cdcTableY - 4, currentX, cdcTableY + cdcTableHeight - 4);

    currentX += scaledCol2;
    doc.line(currentX, cdcTableY - 4, currentX, cdcTableY + cdcTableHeight - 4);

    currentX += scaledCol3;
    doc.line(currentX, cdcTableY - 4, currentX, cdcTableY + cdcTableHeight - 4);

    currentX += scaledCol4;
    doc.line(currentX, cdcTableY - 4, currentX, cdcTableY + cdcTableHeight - 4);

    // Draw horizontal lines for each row
    const cdcRowPositions = [
      cdcTableY,        // Header row
      cdcTableY + 10,   // CDC row
      cdcTableY + 20,   // INDIAN row
      cdcTableY + 30,   // PANAMA row
      cdcTableY + 40,   // LIBERIAN row
      cdcTableY + 50    // OTHERS row
    ];

    cdcRowPositions.forEach((pos, index) => {
      if (index > 0 && index < cdcRowPositions.length) {
        doc.line(marginLeft, pos - 4, pageWidth - marginRight, pos - 4);
      }
    });

    // Table Headers
    doc.setFont(undefined, "bold");
    doc.text("", marginLeft + 5, cdcTableY); // Empty cell
    doc.text("NUMBER", marginLeft + scaledCol1 + 5, cdcTableY);
    doc.text("PLACE OF", marginLeft + scaledCol1 + scaledCol2 + 5, cdcTableY);
    doc.text("DATE OF", marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + 5, cdcTableY);
    doc.text("DATE OF", marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + scaledCol4 + 5, cdcTableY);

    doc.text("ISSUE", marginLeft + scaledCol1 + scaledCol2 + 5, cdcTableY + 4);
    doc.text("ISSUE", marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + 5, cdcTableY + 4);
    doc.text("EXPIRY", marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + scaledCol4 + 5, cdcTableY + 4);

    // Row 1: CDC
    doc.setFont(undefined, "bold");
    doc.text("CDC", marginLeft + 5, cdcTableY + 8);
    doc.setFont(undefined, "normal");
    doc.text(crew?.cdc || "______________", marginLeft + scaledCol1 + 5, cdcTableY + 9);
    doc.text(crew?.cdcPlaceOfIssue || "______________", marginLeft + scaledCol1 + scaledCol2 + 5, cdcTableY + 9);

    const cdcIssueDate = crew?.cdcIssueDate ? new Date(crew.cdcIssueDate).toLocaleDateString() : "______________";
    const cdcExpiryDate = crew?.cdcExpiryDate ? new Date(crew.cdcExpiryDate).toLocaleDateString() : "______________";

    doc.text(cdcIssueDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + 5, cdcTableY + 9);
    doc.text(cdcExpiryDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + scaledCol4 + 5, cdcTableY + 9);

    // Row 2: INDIAN
    doc.setFont(undefined, "bold");
    doc.text("INDIAN", marginLeft + 5, cdcTableY + 18);
    doc.setFont(undefined, "normal");
    doc.text(crew?.cdcIndianNumber || "______________", marginLeft + scaledCol1 + 5, cdcTableY + 19);
    doc.text(crew?.cdcIndianPlaceOfIssue || "______________", marginLeft + scaledCol1 + scaledCol2 + 5, cdcTableY + 19);

    const cdcIndianIssueDate = crew?.cdcIndianIssueDate ? new Date(crew.cdcIndianIssueDate).toLocaleDateString() : "______________";
    const cdcIndianExpiryDate = crew?.cdcIndianExpiryDate ? new Date(crew.cdcIndianExpiryDate).toLocaleDateString() : "______________";

    doc.text(cdcIndianIssueDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + 5, cdcTableY + 19);
    doc.text(cdcIndianExpiryDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + scaledCol4 + 5, cdcTableY + 19);

    // Row 3: PANAMA
    doc.setFont(undefined, "bold");
    doc.text("PANAMA", marginLeft + 5, cdcTableY + 28);
    doc.setFont(undefined, "normal");
    doc.text(crew?.cdcPanamaNumber || "______________", marginLeft + scaledCol1 + 5, cdcTableY + 29);
    doc.text(crew?.cdcPanamaPlaceOfIssue || "______________", marginLeft + scaledCol1 + scaledCol2 + 5, cdcTableY + 29);

    const cdcPanamaIssueDate = crew?.cdcPanamaIssueDate ? new Date(crew.cdcPanamaIssueDate).toLocaleDateString() : "______________";
    const cdcPanamaExpiryDate = crew?.cdcPanamaExpiryDate ? new Date(crew.cdcPanamaExpiryDate).toLocaleDateString() : "______________";

    doc.text(cdcPanamaIssueDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + 5, cdcTableY + 29);
    doc.text(cdcPanamaExpiryDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + scaledCol4 + 5, cdcTableY + 29);

    // Row 4: LIBERIAN
    doc.setFont(undefined, "bold");
    doc.text("LIBERIAN", marginLeft + 5, cdcTableY + 38);
    doc.setFont(undefined, "normal");
    doc.text(crew?.cdcLiberianNumber || "______________", marginLeft + scaledCol1 + 5, cdcTableY + 39);
    doc.text(crew?.cdcLiberianPlaceOfIssue || "______________", marginLeft + scaledCol1 + scaledCol2 + 5, cdcTableY + 39);

    const cdcLiberianIssueDate = crew?.cdcLiberianIssueDate ? new Date(crew.cdcLiberianIssueDate).toLocaleDateString() : "______________";
    const cdcLiberianExpiryDate = crew?.cdcLiberianExpiryDate ? new Date(crew.cdcLiberianExpiryDate).toLocaleDateString() : "______________";

    doc.text(cdcLiberianIssueDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + 5, cdcTableY + 39);
    doc.text(cdcLiberianExpiryDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + scaledCol4 + 5, cdcTableY + 39);

    // Row 5: OTHERS
    doc.setFont(undefined, "bold");
    doc.text("OTHERS", marginLeft + 5, cdcTableY + 48);
    doc.setFont(undefined, "normal");
    doc.text(crew?.cdcOthersNumber || "______________", marginLeft + scaledCol1 + 5, cdcTableY + 49);
    doc.text(crew?.cdcOthersPlaceOfIssue || "______________", marginLeft + scaledCol1 + scaledCol2 + 5, cdcTableY + 49);

    const cdcOthersIssueDate = crew?.cdcOthersIssueDate ? new Date(crew.cdcOthersIssueDate).toLocaleDateString() : "______________";
    const cdcOthersExpiryDate = crew?.cdcOthersExpiryDate ? new Date(crew.cdcOthersExpiryDate).toLocaleDateString() : "______________";

    doc.text(cdcOthersIssueDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + 5, cdcTableY + 49);
    doc.text(cdcOthersExpiryDate, marginLeft + scaledCol1 + scaledCol2 + scaledCol3 + scaledCol4 + 5, cdcTableY + 49);

    y = cdcTableY + cdcTableHeight + 8;

    // Check if we need a new page for section 5
    if (y > pageHeight - 80) {
      doc.addPage();
      pageNumber++;
      y = 20;

      // Add header on new page
      doc.setFontSize(8);
      doc.setFont(undefined, "normal");
      doc.text(`Page ${pageNumber}`, pageWidth - marginRight, 15, { align: "right" });
      doc.line(marginLeft, 18, pageWidth - marginRight, 18);

      // Add title on new page
      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("APPLICATION FORM / PERSONAL DATA UPDATE FORM (CONTINUED)", pageWidth / 2, y, {
        align: "center",
      });
      y += lineHeight + 10;
    }

    // ==============================
    // SECTION 5: LICENCE / CERTIFICATES OF COMPETENCY
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text("5. LICENCE / CERTIFICATES OF COMPETENCY", marginLeft, y);
    y += lineHeight + 2;

    doc.setFontSize(7);

    // Competency Table
    const compTableY = y;
    const compTableHeight = 60; // Height for 4 rows

    // Define column widths for competency table (7 columns)
    const compCol1 = 30; // Issuing authority
    const compCol2 = 25; // Grade
    const compCol3 = 25; // Number
    const compCol4 = 25; // Date of Issue
    const compCol5 = 25; // Date of Expiry
    const compCol6 = 25; // Date Revalidation
    const compCol7 = contentWidth - (compCol1 + compCol2 + compCol3 + compCol4 + compCol5 + compCol6); // STCW 95 with checkboxes

    // Draw table border
    doc.rect(marginLeft, compTableY - 4, contentWidth, compTableHeight);

    // Draw vertical lines
    let compX = marginLeft + compCol1;
    doc.line(compX, compTableY - 4, compX, compTableY + compTableHeight - 4);

    compX += compCol2;
    doc.line(compX, compTableY - 4, compX, compTableY + compTableHeight - 4);

    compX += compCol3;
    doc.line(compX, compTableY - 4, compX, compTableY + compTableHeight - 4);

    compX += compCol4;
    doc.line(compX, compTableY - 4, compX, compTableY + compTableHeight - 4);

    compX += compCol5;
    doc.line(compX, compTableY - 4, compX, compTableY + compTableHeight - 4);

    compX += compCol6;
    doc.line(compX, compTableY - 4, compX, compTableY + compTableHeight - 4);

    // Draw horizontal lines for each row
    const compRowPositions = [
      compTableY,        // Header row
      compTableY + 12,   // INDIAN row
      compTableY + 24,   // PANAMA row
      compTableY + 36,   // OTHERS row
      compTableY + 48    // Empty row
    ];

    compRowPositions.forEach((pos, index) => {
      if (index > 0 && index < compRowPositions.length) {
        doc.line(marginLeft, pos - 4, pageWidth - marginRight, pos - 4);
      }
    });

    // Table Headers
    doc.setFont(undefined, "bold");
    doc.setFontSize(6);
    doc.text("Issuing", marginLeft + 2, compTableY);
    doc.text("Grade", marginLeft + compCol1 + 2, compTableY);
    doc.text("Number", marginLeft + compCol1 + compCol2 + 2, compTableY);
    doc.text("Date of", marginLeft + compCol1 + compCol2 + compCol3 + 2, compTableY);
    doc.text("Date of", marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + 2, compTableY);
    doc.text("Date", marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + compCol5 + 2, compTableY);
    doc.text("STCW 95", marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + compCol5 + compCol6 + 2, compTableY);

    doc.text("authority", marginLeft + 2, compTableY + 3);
    doc.text("", marginLeft + compCol1 + 2, compTableY + 3);
    doc.text("", marginLeft + compCol1 + compCol2 + 2, compTableY + 3);
    doc.text("Issue", marginLeft + compCol1 + compCol2 + compCol3 + 2, compTableY + 3);
    doc.text("Expiry", marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + 2, compTableY + 3);
    doc.text("Revalidation", marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + compCol5 + 2, compTableY + 3);
    doc.text("(Yes/No)", marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + compCol5 + compCol6 + 2, compTableY + 3);

    // Row 1: INDIAN
    doc.setFont(undefined, "bold");
    doc.text("INDIAN", marginLeft + 2, compTableY + 8);
    doc.setFont(undefined, "normal");
    doc.text(crew?.indianIssuingAuthority || "______", marginLeft + compCol1 + 2, compTableY + 8);
    doc.text(crew?.indianGrade || "______", marginLeft + compCol1 + compCol2 + 2, compTableY + 8);
    doc.text(crew?.g || "______", marginLeft + compCol1 + compCol2 + compCol3 + 2, compTableY + 8);

    const indianIssueDate = crew?.indianIssueDate ? new Date(crew.indianIssueDate).toLocaleDateString() : "______";
    const indianExpiryDate = crew?.indianExpiryDate ? new Date(crew.indianExpiryDate).toLocaleDateString() : "______";
    const indianRevalidationDate = crew?.indianRevalidationDate ? new Date(crew.indianRevalidationDate).toLocaleDateString() : "______";

    doc.text(indianIssueDate, marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + 2, compTableY + 8);
    doc.text(indianExpiryDate, marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + compCol5 + 2, compTableY + 8);
    doc.text(indianRevalidationDate, marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + compCol5 + compCol6 + 2, compTableY + 8);

    // STCW 95 checkboxes for INDIAN - properly aligned in the last column
    const stcwX = marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + compCol5 + compCol6;
    doc.text("YES", stcwX + 5, compTableY + 8);
    doc.rect(stcwX + 12, compTableY + 5, 3, 3);
    doc.text("NO", stcwX + 20, compTableY + 8);
    doc.rect(stcwX + 27, compTableY + 5, 3, 3);

    // Row 2: PANAMA
    doc.setFont(undefined, "bold");
    doc.text("PANAMA", marginLeft + 2, compTableY + 20);
    doc.setFont(undefined, "normal");
    doc.text(crew?.panamaIssuingAuthority || "______", marginLeft + compCol1 + 2, compTableY + 20);
    doc.text(crew?.panamaGrade || "______", marginLeft + compCol1 + compCol2 + 2, compTableY + 20);
    doc.text(crew?.panamaNumber || "______", marginLeft + compCol1 + compCol2 + compCol3 + 2, compTableY + 20);

    const panamaIssueDate = crew?.panamaIssueDate ? new Date(crew.panamaIssueDate).toLocaleDateString() : "______";
    const panamaExpiryDate = crew?.panamaExpiryDate ? new Date(crew.panamaExpiryDate).toLocaleDateString() : "______";
    const panamaRevalidationDate = crew?.panamaRevalidationDate ? new Date(crew.panamaRevalidationDate).toLocaleDateString() : "______";

    doc.text(panamaIssueDate, marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + 2, compTableY + 20);
    doc.text(panamaExpiryDate, marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + compCol5 + 2, compTableY + 20);
    doc.text(panamaRevalidationDate, marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + compCol5 + compCol6 + 2, compTableY + 20);

    // STCW 95 checkboxes for PANAMA
    doc.text("YES", stcwX + 5, compTableY + 20);
    doc.rect(stcwX + 12, compTableY + 17, 3, 3);
    doc.text("NO", stcwX + 20, compTableY + 20);
    doc.rect(stcwX + 27, compTableY + 17, 3, 3);

    // Row 3: OTHERS
    doc.setFont(undefined, "bold");
    doc.text("OTHERS", marginLeft + 2, compTableY + 32);
    doc.setFont(undefined, "normal");
    doc.text(crew?.othersIssuingAuthority || "______", marginLeft + compCol1 + 2, compTableY + 32);
    doc.text(crew?.othersGrade || "______", marginLeft + compCol1 + compCol2 + 2, compTableY + 32);
    doc.text(crew?.othersNumber || "______", marginLeft + compCol1 + compCol2 + compCol3 + 2, compTableY + 32);

    const othersIssueDate = crew?.othersIssueDate ? new Date(crew.othersIssueDate).toLocaleDateString() : "______";
    const othersExpiryDate = crew?.othersExpiryDate ? new Date(crew.othersExpiryDate).toLocaleDateString() : "______";
    const othersRevalidationDate = crew?.othersRevalidationDate ? new Date(crew.othersRevalidationDate).toLocaleDateString() : "______";

    doc.text(othersIssueDate, marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + 2, compTableY + 32);
    doc.text(othersExpiryDate, marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + compCol5 + 2, compTableY + 32);
    doc.text(othersRevalidationDate, marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + compCol5 + compCol6 + 2, compTableY + 32);

    // STCW 95 checkboxes for OTHERS
    doc.text("YES", stcwX + 5, compTableY + 32);
    doc.rect(stcwX + 12, compTableY + 29, 3, 3);
    doc.text("NO", stcwX + 20, compTableY + 32);
    doc.rect(stcwX + 27, compTableY + 29, 3, 3);

    // Row 4: Empty row for additional entries
    doc.setFont(undefined, "normal");
    doc.text("______", marginLeft + compCol1 + 2, compTableY + 44);
    doc.text("______", marginLeft + compCol1 + compCol2 + 2, compTableY + 44);
    doc.text("______", marginLeft + compCol1 + compCol2 + compCol3 + 2, compTableY + 44);
    doc.text("______", marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + 2, compTableY + 44);
    doc.text("______", marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + compCol5 + 2, compTableY + 44);
    doc.text("______", marginLeft + compCol1 + compCol2 + compCol3 + compCol4 + compCol5 + compCol6 + 2, compTableY + 44);

    // STCW 95 checkboxes for empty row
    doc.text("YES", stcwX + 5, compTableY + 44);
    doc.rect(stcwX + 12, compTableY + 41, 3, 3);
    doc.text("NO", stcwX + 20, compTableY + 44);
    doc.rect(stcwX + 27, compTableY + 41, 3, 3);

    y = compTableY + compTableHeight + 15;

    // ==============================
    // FOOTER
    // ==============================
    doc.setFontSize(7);
    doc.setTextColor(100);
    doc.setFont(undefined, "normal");
    doc.text(
      "This document is electronically generated and does not require a physical signature.",
      pageWidth / 2,
      pageHeight - 8,
      { align: "center" }
    );

    // Add page number
    doc.setFontSize(8);
    doc.setTextColor(0);
    doc.text(`Page ${pageNumber}`, pageWidth - marginRight, pageHeight - 15, { align: "right" });

    doc.save("Application_Form_Page2.pdf");
  };

  const generateApplicationFormThree = async (crew, agentLogo, selectedAgentData) => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const marginLeft = 15;
    const marginRight = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;
    const lineHeight = 6;

    let y = 20;
    let pageNumber = 1;

    // ==============================
    // HEADER SECTION
    // ==============================

    // Logo (Top Left) - taken from selectedAgentData
    if (selectedAgentData?.logo?.filePath) {
      try {
        // Clean up the file path - replace backslashes with forward slashes
        const cleanFilePath = selectedAgentData.logo.filePath.replace(/\\/g, '/');

        const logoUrl = `${baseURL}/${cleanFilePath}`;

        // Determine image format from file extension
        const fileExtension = selectedAgentData.logo.filePath.split('.').pop().toLowerCase();
        let imageFormat = 'JPEG'; // Default format

        // Map common extensions to jsPDF supported formats
        const formatMap = {
          'jpg': 'JPEG',
          'jpeg': 'JPEG',
          'png': 'PNG',
          'gif': 'GIF',
          'bmp': 'BMP',
          'webp': 'WEBP'
        };

        imageFormat = formatMap[fileExtension] || 'JPEG';

        doc.addImage(logoUrl, imageFormat, marginLeft, 10, 35, 18);
      } catch (err) {
        console.log("Logo error:", err);
      }
    }
    // Company Information (Top Right)
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(
      selectedAgentData?.companyName || "SOULITE SHIP MANAGEMENT PVT LTD",
      pageWidth - marginRight,
      12,
      { align: "right" }
    );

    doc.setFontSize(9);
    doc.setFont(undefined, "normal");

    // Address
    const address = selectedAgentData?.address || "D-22, First Floor, Office No-03, Sector-03, Noida (UP) 201301";
    doc.text(address, pageWidth - marginRight, 18, {
      align: "right",
    });

    // Email
    doc.text(
      `Email: ${selectedAgentData?.email || "hr@souliteshipmanagement.com"}`,
      pageWidth - marginRight,
      24,
      { align: "right" }
    );

    // Mobile Number
    doc.text(
      `Tel: ${selectedAgentData?.mobileNumber || "+917303611301"}`,
      pageWidth - marginRight,
      30,
      { align: "right" }
    );

    // Divider Line
    doc.line(marginLeft, 38, pageWidth - marginRight, 38);

    y = 48;
    // ==============================
    // TITLE
    // ==============================
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("APPLICATION FORM / PERSONAL DATA UPDATE FORM (PAGE 3)", pageWidth / 2, y, {
      align: "center",
    });

    y += lineHeight + 10;

    // ==============================
    // SECTION 6: STCW AND OTHER CERTIFICATES
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text("6. STCW AND OTHER CERTIFICATES", marginLeft, y);
    y += lineHeight + 2;

    doc.setFontSize(7);

    // STCW Certificates Table
    const stcwTableY = y;

    // Define column widths (6 columns)
    const col1Width = 55; // STCW Courses
    const col2Width = 25; // Certificate No
    const col3Width = 20; // Date of Issue
    const col4Width = 20; // Date of Expiry
    const col5Width = 25; // Place of Issue
    const col6Width = contentWidth - (col1Width + col2Width + col3Width + col4Width + col5Width); // Issuing Authority

    // List of STCW courses
    const stcwCourses = [
      "PRE SEA-TRAINING",
      "Personal Safety and Social Responsibility (PSSR)",
      "Medical First Aid (MFA)",
      "Elementary First Aid (EFA)",
      "Personal Survival Techniques (PST)",
      "Proficiency in Survival Craft and Rescue Boats (PSCRB)",
      "Fire Prevention and Fire Fighting (FPFF)",
      "Medicare",
      "Advanced Fire Fighting (AFF)",
      "Radar Observer Simulator Course (ROC)",
      "Automatic Radar Plotting Aids (ARPA)",
      "Global Maritime Distress and Safety System (GMDSS)",
      "GMDSS Endorsement",
      "Radar ARPA and Navigation Simulation Course (RANSCO / NARAS)",
      "Navigational Control",
      "Certificate Revalidation Course",
      "Simulator Training",
      "Bridge Team Management (BTM)",
      "Electronic Chart Display and Information System (ECDIS)",
      "Bridge Resource Management (BRM/ERM/VRM)",
      "Large Vessel Handling Simulator Course",
      "Engine Room Simulator",
      "ICE Navigation",
      "Oil Tanker Familiarization",
      "Specialized Training Program on Oil Tanker Operation (TASCO)"
    ];

    const rowCount = stcwCourses.length;
    const rowHeight = 10; // Increased height per row to accommodate two lines
    const tableHeight = rowCount * rowHeight + 8; // Add header row height

    // Helper function to split text into multiple lines
    const splitTextIntoLines = (text, maxWidth) => {
      return doc.splitTextToSize(text, maxWidth);
    };

    // Check if we need multiple pages
    let currentY = stcwTableY;
    let startIndex = 0;
    let rowsPerPage = Math.floor((pageHeight - currentY - 30) / rowHeight); // Reserve space for header and footer

    while (startIndex < rowCount) {
      const endIndex = Math.min(startIndex + rowsPerPage, rowCount);
      const currentRows = endIndex - startIndex;
      const currentTableHeight = currentRows * rowHeight + 8;

      // Draw table border
      doc.rect(marginLeft, currentY - 4, contentWidth, currentTableHeight);

      // Draw vertical lines
      let currentX = marginLeft + col1Width;
      doc.line(currentX, currentY - 4, currentX, currentY + currentTableHeight - 4);

      currentX += col2Width;
      doc.line(currentX, currentY - 4, currentX, currentY + currentTableHeight - 4);

      currentX += col3Width;
      doc.line(currentX, currentY - 4, currentX, currentY + currentTableHeight - 4);

      currentX += col4Width;
      doc.line(currentX, currentY - 4, currentX, currentY + currentTableHeight - 4);

      currentX += col5Width;
      doc.line(currentX, currentY - 4, currentX, currentY + currentTableHeight - 4);

      // Draw horizontal line for header
      doc.line(marginLeft, currentY + 4, pageWidth - marginRight, currentY + 4);

      // Table Headers
      doc.setFont(undefined, "bold");
      doc.setFontSize(7);
      doc.text("STCW Courses", marginLeft + 2, currentY);
      doc.text("Certificate No", marginLeft + col1Width + 2, currentY);
      doc.text("Date of", marginLeft + col1Width + col2Width + 2, currentY);
      doc.text("Date of", marginLeft + col1Width + col2Width + col3Width + 2, currentY);
      doc.text("Place of", marginLeft + col1Width + col2Width + col3Width + col4Width + 2, currentY);
      doc.text("Issuing Authority/", marginLeft + col1Width + col2Width + col3Width + col4Width + col5Width + 2, currentY);

      doc.text("Issue", marginLeft + col1Width + col2Width + 2, currentY + 3);
      doc.text("Expiry", marginLeft + col1Width + col2Width + col3Width + 2, currentY + 3);
      doc.text("Issue", marginLeft + col1Width + col2Width + col3Width + col4Width + 2, currentY + 3);
      doc.text("INSTITUTE", marginLeft + col1Width + col2Width + col3Width + col4Width + col5Width + 2, currentY + 3);

      // Draw rows
      doc.setFont(undefined, "normal");

      for (let i = 0; i < currentRows; i++) {
        const rowIndex = startIndex + i;
        const rowY = currentY + 8 + (i * rowHeight);

        // Draw horizontal line between rows (except after last row)
        if (i < currentRows - 1) {
          doc.line(marginLeft, rowY + rowHeight - 2, pageWidth - marginRight, rowY + rowHeight - 2);
        }

        // Course Name - Split into multiple lines if needed
        const courseName = stcwCourses[rowIndex];
        const maxWidth = col1Width - 4; // Leave some padding
        const lines = splitTextIntoLines(courseName, maxWidth);

        // Draw course name lines
        lines.forEach((line, lineIndex) => {
          doc.text(line, marginLeft + 2, rowY + (lineIndex * 3));
        });

        // Certificate Number
        doc.text("______________", marginLeft + col1Width + 2, rowY + 1);

        // Date of Issue
        doc.text("______________", marginLeft + col1Width + col2Width + 2, rowY + 1);

        // Date of Expiry
        doc.text("______________", marginLeft + col1Width + col2Width + col3Width + 2, rowY + 1);

        // Place of Issue
        doc.text("______________", marginLeft + col1Width + col2Width + col3Width + col4Width + 2, rowY + 1);

        // Issuing Authority
        doc.text("______________", marginLeft + col1Width + col2Width + col3Width + col4Width + col5Width + 2, rowY + 1);
      }

      currentY += currentTableHeight + 8;
      startIndex = endIndex;

      // Add new page if there are more rows
      if (startIndex < rowCount) {
        doc.addPage();
        pageNumber++;
        currentY = 20;

        // Add header on new page
        doc.setFontSize(14);
        doc.setFont(undefined, "bold");
        doc.text(selectedAgentData?.companyName, pageWidth / 2, currentY, { align: "center" });
        currentY += lineHeight;

        doc.setFontSize(8);
        doc.setFont(undefined, "normal");
        // doc.text("Email: admin@shreyasun.com - Website: www.shreyasun.com - Tel: 0522-3375868", pageWidth / 2, currentY, { align: "center" });
        currentY += lineHeight + 4;

        if (agentLogo) {
          try {
            doc.addImage(agentLogo, "JPEG", marginLeft, currentY - 15, 25, 12);
          } catch (err) {
            console.log("Logo error:", err);
          }
        }

        doc.line(marginLeft, currentY, pageWidth - marginRight, currentY);
        currentY += lineHeight + 2;

        doc.setFontSize(12);
        doc.setFont(undefined, "bold");
        doc.text("APPLICATION FORM / PERSONAL DATA UPDATE FORM (CONTINUED)", pageWidth / 2, currentY, {
          align: "center",
        });
        currentY += lineHeight + 10;

        doc.setFont(undefined, "bold");
        doc.setFontSize(10);
        doc.text("6. STCW AND OTHER CERTIFICATES (CONTINUED)", marginLeft, currentY);
        currentY += lineHeight + 2;
      }
    }

    y = currentY;

    // ==============================
    // FOOTER
    // ==============================
    doc.setFontSize(7);
    doc.setTextColor(100);
    doc.setFont(undefined, "normal");
    doc.text(
      "This document is electronically generated and does not require a physical signature.",
      pageWidth / 2,
      pageHeight - 8,
      { align: "center" }
    );

    // Add page number
    doc.setFontSize(8);
    doc.setTextColor(0);
    doc.text(`Page ${pageNumber}`, pageWidth - marginRight, pageHeight - 15, { align: "right" });

    doc.save("Application_Form_Page3.pdf");
  };
  const generateApplicationFormFour = async (crew, agentLogo, selectedAgentData) => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });


    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const marginLeft = 15;
    const marginRight = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;
    const lineHeight = 6;

    let y = 20;
    let pageNumber = 1;

    // ==============================
    // HEADER SECTION
    // ==============================

    // Logo (Top Left) - taken from selectedAgentData
    if (selectedAgentData?.logo?.filePath) {
      try {
        // Clean up the file path - replace backslashes with forward slashes
        const cleanFilePath = selectedAgentData.logo.filePath.replace(/\\/g, '/');

        const logoUrl = `${baseURL}/${cleanFilePath}`;

        // Determine image format from file extension
        const fileExtension = selectedAgentData.logo.filePath.split('.').pop().toLowerCase();
        let imageFormat = 'JPEG'; // Default format

        // Map common extensions to jsPDF supported formats
        const formatMap = {
          'jpg': 'JPEG',
          'jpeg': 'JPEG',
          'png': 'PNG',
          'gif': 'GIF',
          'bmp': 'BMP',
          'webp': 'WEBP'
        };

        imageFormat = formatMap[fileExtension] || 'JPEG';

        doc.addImage(logoUrl, imageFormat, marginLeft, 10, 35, 18);
      } catch (err) {
        console.log("Logo error:", err);
      }
    }
    // Company Information (Top Right)
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(
      selectedAgentData?.companyName || "SOULITE SHIP MANAGEMENT PVT LTD",
      pageWidth - marginRight,
      12,
      { align: "right" }
    );

    doc.setFontSize(9);
    doc.setFont(undefined, "normal");

    // Address
    const address = selectedAgentData?.address || "D-22, First Floor, Office No-03, Sector-03, Noida (UP) 201301";
    doc.text(address, pageWidth - marginRight, 18, {
      align: "right",
    });

    // Email
    doc.text(
      `Email: ${selectedAgentData?.email || "hr@souliteshipmanagement.com"}`,
      pageWidth - marginRight,
      24,
      { align: "right" }
    );

    // Mobile Number
    doc.text(
      `Tel: ${selectedAgentData?.mobileNumber || "+917303611301"}`,
      pageWidth - marginRight,
      30,
      { align: "right" }
    );

    // Divider Line
    doc.line(marginLeft, 38, pageWidth - marginRight, 38);

    y = 48;
    // ==============================
    // TITLE
    // ==============================
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("APPLICATION FORM / PERSONAL DATA UPDATE FORM (PAGE 4)", pageWidth / 2, y, {
      align: "center",
    });

    y += lineHeight + 10;

    // ==============================
    // ADDITIONAL STCW COURSES
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text("ADDITIONAL STCW COURSES (CONTINUED)", marginLeft, y);
    y += lineHeight + 2;

    doc.setFontSize(7);

    // Additional courses list
    const additionalCourses = [
      "Specialized Training Program on Chemical Tanker Operations (CHEMCO)",
      "Specialized Training Program on Liquefied Gas Tanker Operations (GASCO)",
      "Dangerous Cargo Endorsement (DCE) GAS/CHEM/OIL",
      "Helicopter Underwater Escape Techniques",
      "Ship Security Officer (SSO) / STS DSD",
      "International Ship and port Facility Security ISPS (SOC)",
      "6G, 3G, 2G Welder Certificate"
    ];

    // Define column widths for courses table (adjusted for portrait)
    const courseCol1 = 50; // Course Name (reduced from 70)
    const courseCol2 = 25; // Certificate No (reduced from 30)
    const courseCol3 = 20; // Date of Issue (reduced from 25)
    const courseCol4 = 20; // Date of Expiry (reduced from 25)
    const courseCol5 = 25; // Place of Issue (reduced from 30)
    const courseCol6 = contentWidth - (courseCol1 + courseCol2 + courseCol3 + courseCol4 + courseCol5); // Issuing Authority

    // Helper function to split text into multiple lines
    const splitTextIntoLines = (text, maxWidth) => {
      return doc.splitTextToSize(text, maxWidth);
    };

    // Calculate dynamic row heights based on course name length
    const courseRowHeights = additionalCourses.map(course => {
      const maxWidth = courseCol1 - 4; // Leave some padding
      const lines = splitTextIntoLines(course, maxWidth);
      return lines.length * 4 + 2; // 4mm per line + padding
    });

    // Draw table border
    const courseTableY = y;
    const courseTableHeight = courseRowHeights.reduce((sum, height) => sum + height, 0) + 8; // +8 for header

    doc.rect(marginLeft, courseTableY - 4, contentWidth, courseTableHeight);

    // Draw vertical lines
    let currentX = marginLeft + courseCol1;
    doc.line(currentX, courseTableY - 4, currentX, courseTableY + courseTableHeight - 4);

    currentX += courseCol2;
    doc.line(currentX, courseTableY - 4, currentX, courseTableY + courseTableHeight - 4);

    currentX += courseCol3;
    doc.line(currentX, courseTableY - 4, currentX, courseTableY + courseTableHeight - 4);

    currentX += courseCol4;
    doc.line(currentX, courseTableY - 4, currentX, courseTableY + courseTableHeight - 4);

    currentX += courseCol5;
    doc.line(currentX, courseTableY - 4, currentX, courseTableY + courseTableHeight - 4);

    // Draw horizontal line for header
    doc.line(marginLeft, courseTableY + 4, pageWidth - marginRight, courseTableY + 4);

    // Table Headers
    doc.setFont(undefined, "bold");
    doc.setFontSize(6);
    doc.text("STCW Courses", marginLeft + 2, courseTableY);
    doc.text("Certificate No", marginLeft + courseCol1 + 2, courseTableY);
    doc.text("Date of", marginLeft + courseCol1 + courseCol2 + 2, courseTableY);
    doc.text("Date of", marginLeft + courseCol1 + courseCol2 + courseCol3 + 2, courseTableY);
    doc.text("Place of", marginLeft + courseCol1 + courseCol2 + courseCol3 + courseCol4 + 2, courseTableY);
    doc.text("Issuing Authority/", marginLeft + courseCol1 + courseCol2 + courseCol3 + courseCol4 + courseCol5 + 2, courseTableY);

    doc.text("Issue", marginLeft + courseCol1 + courseCol2 + 2, courseTableY + 3);
    doc.text("Expiry", marginLeft + courseCol1 + courseCol2 + courseCol3 + 2, courseTableY + 3);
    doc.text("Issue", marginLeft + courseCol1 + courseCol2 + courseCol3 + courseCol4 + 2, courseTableY + 3);
    doc.text("INSTITUTE", marginLeft + courseCol1 + courseCol2 + courseCol3 + courseCol4 + courseCol5 + 2, courseTableY + 3);

    // Draw rows for additional courses
    doc.setFont(undefined, "normal");
    let currentRowY = courseTableY + 8;

    for (let i = 0; i < additionalCourses.length; i++) {
      const courseName = additionalCourses[i];
      const maxWidth = courseCol1 - 4; // Leave some padding
      const lines = splitTextIntoLines(courseName, maxWidth);
      const rowHeight = lines.length * 4 + 2;

      // Draw horizontal line between rows (except after last row)
      if (i < additionalCourses.length - 1) {
        doc.line(marginLeft, currentRowY + rowHeight - 2, pageWidth - marginRight, currentRowY + rowHeight - 2);
      }

      // Course Name - Split into multiple lines
      lines.forEach((line, lineIndex) => {
        doc.text(line, marginLeft + 2, currentRowY + (lineIndex * 4));
      });

      // Calculate vertical offset based on number of lines for better centering
      const verticalOffset = lines.length === 1 ? 1 : (lines.length === 2 ? 2 : 3);

      // Certificate Number
      doc.text("______________", marginLeft + courseCol1 + 2, currentRowY + verticalOffset);

      // Date of Issue
      doc.text("______________", marginLeft + courseCol1 + courseCol2 + 2, currentRowY + verticalOffset);

      // Date of Expiry
      doc.text("______________", marginLeft + courseCol1 + courseCol2 + courseCol3 + 2, currentRowY + verticalOffset);

      // Place of Issue
      doc.text("______________", marginLeft + courseCol1 + courseCol2 + courseCol3 + courseCol4 + 2, currentRowY + verticalOffset);

      // Issuing Authority
      doc.text("______________", marginLeft + courseCol1 + courseCol2 + courseCol3 + courseCol4 + courseCol5 + 2, currentRowY + verticalOffset);

      currentRowY += rowHeight;
    }

    y = courseTableY + courseTableHeight + 10;

    // ==============================
    // SEA EXPERIENCE SECTION
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text("07. SEA EXPERIENCE: (Most recent at bottom)", marginLeft, y);
    y += lineHeight + 2;

    doc.setFontSize(8);

    // Major Incidence text
    doc.setFont(undefined, "bold");
    doc.text("Any Major Incidence / Observation / Special Experience for reporting:", marginLeft, y);
    y += lineHeight + 2;

    // Sea Experience Table
    const expTableY = y;

    // Define column widths for experience table (10 columns) - adjusted for portrait
    const expCol1 = 25; // COMPANY (reduced from 30)
    const expCol2 = 25; // VESSEL NAME (reduced from 30)
    const expCol3 = 22; // TYPE OF VESSEL (reduced from 25)
    const expCol4 = 12; // FLAG (reduced from 15)
    const expCol5 = 12; // GRT (reduced from 15)
    const expCol6 = 12; // BHP (reduced from 15)
    const expCol7 = 18; // RANK (reduced from 20)
    const expCol8 = 12; // FROM (reduced from 15)
    const expCol9 = 12; // TO (reduced from 15)
    const expCol10 = contentWidth - (expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + expCol7 + expCol8 + expCol9); // REASON FOR SIGN-OFF

    // Number of experience rows (show 5 rows initially)
    const expRowCount = 5;
    const expRowHeight = 8;
    const expTableHeight = (expRowCount + 1) * expRowHeight; // +1 for header

    // Draw table border
    doc.rect(marginLeft, expTableY - 4, contentWidth, expTableHeight);

    // Draw vertical lines
    let expX = marginLeft + expCol1;
    doc.line(expX, expTableY - 4, expX, expTableY + expTableHeight - 4);

    expX += expCol2;
    doc.line(expX, expTableY - 4, expX, expTableY + expTableHeight - 4);

    expX += expCol3;
    doc.line(expX, expTableY - 4, expX, expTableY + expTableHeight - 4);

    expX += expCol4;
    doc.line(expX, expTableY - 4, expX, expTableY + expTableHeight - 4);

    expX += expCol5;
    doc.line(expX, expTableY - 4, expX, expTableY + expTableHeight - 4);

    expX += expCol6;
    doc.line(expX, expTableY - 4, expX, expTableY + expTableHeight - 4);

    expX += expCol7;
    doc.line(expX, expTableY - 4, expX, expTableY + expTableHeight - 4);

    expX += expCol8;
    doc.line(expX, expTableY - 4, expX, expTableY + expTableHeight - 4);

    expX += expCol9;
    doc.line(expX, expTableY - 4, expX, expTableY + expTableHeight - 4);

    // Draw horizontal line for header
    doc.line(marginLeft, expTableY + 4, pageWidth - marginRight, expTableY + 4);

    // Table Headers
    doc.setFont(undefined, "bold");
    doc.setFontSize(5.5); // Reduced font size for headers
    doc.text("COMPANY", marginLeft + 1, expTableY);
    doc.text("VESSEL", marginLeft + expCol1 + 1, expTableY);
    doc.text("TYPE", marginLeft + expCol1 + expCol2 + 1, expTableY);
    doc.text("FLAG", marginLeft + expCol1 + expCol2 + expCol3 + 1, expTableY);
    doc.text("GRT", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + 1, expTableY);
    doc.text("BHP", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + 1, expTableY);
    doc.text("RANK", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + 1, expTableY);
    doc.text("FROM", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + expCol7 + 1, expTableY);
    doc.text("TO", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + expCol7 + expCol8 + 1, expTableY);
    doc.text("REASON", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + expCol7 + expCol8 + expCol9 + 1, expTableY);

    doc.text("NAME", marginLeft + expCol1 + 1, expTableY + 3);
    doc.text("VESSEL", marginLeft + expCol1 + expCol2 + 1, expTableY + 3);
    doc.text("SIGN-OFF", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + expCol7 + expCol8 + expCol9 + 1, expTableY + 3);

    // Get experience data from crew if available
    const experiences = crew?.experiences || [];

    // Draw experience rows
    doc.setFont(undefined, "normal");
    doc.setFontSize(6); // Reduced font size for data

    for (let i = 0; i < expRowCount; i++) {
      const rowY = expTableY + 8 + (i * expRowHeight);
      const expData = experiences[i] || null;

      // Draw horizontal line between rows
      if (i < expRowCount - 1) {
        doc.line(marginLeft, rowY + 6, pageWidth - marginRight, rowY + 6);
      }

      // COMPANY
      doc.text(expData?.companyName || "______", marginLeft + 1, rowY);

      // VESSEL NAME
      doc.text(expData?.vesselName || "______", marginLeft + expCol1 + 1, rowY);

      // TYPE OF VESSEL
      doc.text(expData?.vesselType || "______", marginLeft + expCol1 + expCol2 + 1, rowY);

      // FLAG
      doc.text(expData?.flag || "____", marginLeft + expCol1 + expCol2 + expCol3 + 1, rowY);

      // GRT
      doc.text(expData?.grt ? expData.grt.toString() : "____", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + 1, rowY);

      // BHP
      doc.text(expData?.bhp ? expData.bhp.toString() : "____", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + 1, rowY);

      // RANK
      doc.text(expData?.rank || "______", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + 1, rowY);

      // FROM
      const fromDate = expData?.from ? new Date(expData.from).toLocaleDateString() : "____";
      doc.text(fromDate, marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + expCol7 + 1, rowY);

      // TO
      const toDate = expData?.to ? new Date(expData.to).toLocaleDateString() : "____";
      doc.text(toDate, marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + expCol7 + expCol8 + 1, rowY);

      // REASON FOR SIGN-OFF
      doc.text(expData?.reasonForSignOff || "______", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + expCol7 + expCol8 + expCol9 + 1, rowY);
    }

    y = expTableY + expTableHeight + 10;

    // Check if we need a new page for additional experience rows
    if (experiences.length > expRowCount) {
      doc.addPage();
      pageNumber++;
      y = 20;

      // Add header on new page
      doc.setFontSize(14);
      doc.setFont(undefined, "bold");
      doc.text(selectedAgentData?.companyName, pageWidth / 2, y, { align: "center" });
      y += lineHeight;

      doc.setFontSize(8);
      doc.setFont(undefined, "normal");
      // doc.text("Email: admin@shreyasun.com - Website: www.shreyasun.com - Tel: 0522-3375868", pageWidth / 2, y, { align: "center" });
      y += lineHeight + 4;

      if (agentLogo) {
        try {
          doc.addImage(agentLogo, "JPEG", marginLeft, y - 15, 25, 12);
        } catch (err) {
          console.log("Logo error:", err);
        }
      }

      doc.line(marginLeft, y, pageWidth - marginRight, y);
      y += lineHeight + 2;

      doc.setFontSize(12);
      doc.setFont(undefined, "bold");
      doc.text("APPLICATION FORM / PERSONAL DATA UPDATE FORM (CONTINUED)", pageWidth / 2, y, {
        align: "center",
      });
      y += lineHeight + 10;

      doc.setFont(undefined, "bold");
      doc.setFontSize(10);
      doc.text("07. SEA EXPERIENCE (CONTINUED)", marginLeft, y);
      y += lineHeight + 2;

      // Draw additional experience rows
      const additionalExpTableY = y;
      const additionalExpCount = experiences.length - expRowCount;
      const additionalExpHeight = (additionalExpCount + 1) * expRowHeight;

      doc.rect(marginLeft, additionalExpTableY - 4, contentWidth, additionalExpHeight);

      // Draw vertical lines (same as before)
      let additionalExpX = marginLeft + expCol1;
      doc.line(additionalExpX, additionalExpTableY - 4, additionalExpX, additionalExpTableY + additionalExpHeight - 4);

      additionalExpX += expCol2;
      doc.line(additionalExpX, additionalExpTableY - 4, additionalExpX, additionalExpTableY + additionalExpHeight - 4);

      additionalExpX += expCol3;
      doc.line(additionalExpX, additionalExpTableY - 4, additionalExpX, additionalExpTableY + additionalExpHeight - 4);

      additionalExpX += expCol4;
      doc.line(additionalExpX, additionalExpTableY - 4, additionalExpX, additionalExpTableY + additionalExpHeight - 4);

      additionalExpX += expCol5;
      doc.line(additionalExpX, additionalExpTableY - 4, additionalExpX, additionalExpTableY + additionalExpHeight - 4);

      additionalExpX += expCol6;
      doc.line(additionalExpX, additionalExpTableY - 4, additionalExpX, additionalExpTableY + additionalExpHeight - 4);

      additionalExpX += expCol7;
      doc.line(additionalExpX, additionalExpTableY - 4, additionalExpX, additionalExpTableY + additionalExpHeight - 4);

      additionalExpX += expCol8;
      doc.line(additionalExpX, additionalExpTableY - 4, additionalExpX, additionalExpTableY + additionalExpHeight - 4);

      additionalExpX += expCol9;
      doc.line(additionalExpX, additionalExpTableY - 4, additionalExpX, additionalExpTableY + additionalExpHeight - 4);

      // Draw horizontal line for header
      doc.line(marginLeft, additionalExpTableY + 4, pageWidth - marginRight, additionalExpTableY + 4);

      // Table Headers (simplified for continuation)
      doc.setFont(undefined, "bold");
      doc.setFontSize(5.5);
      doc.text("COMPANY", marginLeft + 1, additionalExpTableY);
      doc.text("VESSEL", marginLeft + expCol1 + 1, additionalExpTableY);
      doc.text("TYPE", marginLeft + expCol1 + expCol2 + 1, additionalExpTableY);
      doc.text("FLAG", marginLeft + expCol1 + expCol2 + expCol3 + 1, additionalExpTableY);
      doc.text("GRT", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + 1, additionalExpTableY);
      doc.text("BHP", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + 1, additionalExpTableY);
      doc.text("RANK", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + 1, additionalExpTableY);
      doc.text("FROM", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + expCol7 + 1, additionalExpTableY);
      doc.text("TO", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + expCol7 + expCol8 + 1, additionalExpTableY);
      doc.text("REASON", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + expCol7 + expCol8 + expCol9 + 1, additionalExpTableY);

      doc.setFont(undefined, "normal");
      doc.setFontSize(6);

      for (let i = 0; i < additionalExpCount; i++) {
        const rowY = additionalExpTableY + 8 + (i * expRowHeight);
        const expData = experiences[expRowCount + i];

        // Draw horizontal line between rows
        if (i < additionalExpCount - 1) {
          doc.line(marginLeft, rowY + 6, pageWidth - marginRight, rowY + 6);
        }

        doc.text(expData?.companyName || "______", marginLeft + 1, rowY);
        doc.text(expData?.vesselName || "______", marginLeft + expCol1 + 1, rowY);
        doc.text(expData?.vesselType || "______", marginLeft + expCol1 + expCol2 + 1, rowY);
        doc.text(expData?.flag || "____", marginLeft + expCol1 + expCol2 + expCol3 + 1, rowY);
        doc.text(expData?.grt ? expData.grt.toString() : "____", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + 1, rowY);
        doc.text(expData?.bhp ? expData.bhp.toString() : "____", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + 1, rowY);
        doc.text(expData?.rank || "______", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + 1, rowY);

        const fromDate = expData?.from ? new Date(expData.from).toLocaleDateString() : "____";
        doc.text(fromDate, marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + expCol7 + 1, rowY);

        const toDate = expData?.to ? new Date(expData.to).toLocaleDateString() : "____";
        doc.text(toDate, marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + expCol7 + expCol8 + 1, rowY);

        doc.text(expData?.reasonForSignOff || "______", marginLeft + expCol1 + expCol2 + expCol3 + expCol4 + expCol5 + expCol6 + expCol7 + expCol8 + expCol9 + 1, rowY);
      }
    }

    // ==============================
    // FOOTER
    // ==============================
    doc.setFontSize(7);
    doc.setTextColor(100);
    doc.setFont(undefined, "normal");
    doc.text(
      "This document is electronically generated and does not require a physical signature.",
      pageWidth / 2,
      pageHeight - 8,
      { align: "center" }
    );

    // Add page number
    doc.setFontSize(8);
    doc.setTextColor(0);
    doc.text(`Page ${pageNumber}`, pageWidth - marginRight, pageHeight - 15, { align: "right" });

    doc.save("Application_Form_Page4.pdf");
  };

  const generateApplicationFormFive = async (crew, agentLogo, selectedAgentData) => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });


    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const marginLeft = 15;
    const marginRight = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;
    const lineHeight = 6;

    let y = 20;
    let pageNumber = 1;

    // ==============================
    // HEADER SECTION
    // ==============================

    // Logo (Top Left) - taken from selectedAgentData
    if (selectedAgentData?.logo?.filePath) {
      try {
        // Clean up the file path - replace backslashes with forward slashes
        const cleanFilePath = selectedAgentData.logo.filePath.replace(/\\/g, '/');

        const logoUrl = `${baseURL}/${cleanFilePath}`;

        // Determine image format from file extension
        const fileExtension = selectedAgentData.logo.filePath.split('.').pop().toLowerCase();
        let imageFormat = 'JPEG'; // Default format

        // Map common extensions to jsPDF supported formats
        const formatMap = {
          'jpg': 'JPEG',
          'jpeg': 'JPEG',
          'png': 'PNG',
          'gif': 'GIF',
          'bmp': 'BMP',
          'webp': 'WEBP'
        };

        imageFormat = formatMap[fileExtension] || 'JPEG';

        doc.addImage(logoUrl, imageFormat, marginLeft, 10, 35, 18);
      } catch (err) {
        console.log("Logo error:", err);
      }
    }

    // Company Information (Top Right)
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(
      selectedAgentData?.companyName || "SOULITE SHIP MANAGEMENT PVT LTD",
      pageWidth - marginRight,
      12,
      { align: "right" }
    );

    doc.setFontSize(9);
    doc.setFont(undefined, "normal");

    // Address
    const address = selectedAgentData?.address || "D-22, First Floor, Office No-03, Sector-03, Noida (UP) 201301";
    doc.text(address, pageWidth - marginRight, 18, {
      align: "right",
    });

    // Email
    doc.text(
      `Email: ${selectedAgentData?.email || "hr@souliteshipmanagement.com"}`,
      pageWidth - marginRight,
      24,
      { align: "right" }
    );

    // Mobile Number
    doc.text(
      `Tel: ${selectedAgentData?.mobileNumber || "+917303611301"}`,
      pageWidth - marginRight,
      30,
      { align: "right" }
    );

    // Divider Line
    doc.line(marginLeft, 38, pageWidth - marginRight, 38);

    y = 48;

    // ==============================
    // TITLE
    // ==============================
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("APPLICATION FORM / PERSONAL DATA UPDATE FORM (PAGE 5)", pageWidth / 2, y, {
      align: "center",
    });

    y += lineHeight + 10;

    // ==============================
    // SECTION 8: HOW DID YOU COME TO KNOW ABOUT US?
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(8);
    doc.text("8. HOW DID YOU COME TO KNOW ABOUT US? (Please tick the appropriate medium) √", marginLeft, y);
    y += lineHeight + 4;

    doc.setFontSize(9);

    // Create referral source table
    const referralTableY = y;
    const referralTableHeight = 40;

    // Draw table border
    doc.rect(marginLeft, referralTableY - 4, contentWidth, referralTableHeight);

    // Draw vertical line between columns
    const referralColWidth = contentWidth / 3;
    doc.line(marginLeft + referralColWidth, referralTableY - 4, marginLeft + referralColWidth, referralTableY + referralTableHeight - 4);
    doc.line(marginLeft + (referralColWidth * 2), referralTableY - 4, marginLeft + (referralColWidth * 2), referralTableY + referralTableHeight - 4);

    // Draw horizontal lines
    doc.line(marginLeft, referralTableY + 12, pageWidth - marginRight, referralTableY + 12);
    doc.line(marginLeft, referralTableY + 26, pageWidth - marginRight, referralTableY + 26);

    // Row 1
    // A: Company presentation/seminar
    doc.setFont(undefined, "bold");
    doc.text("A", marginLeft + 5, referralTableY);
    doc.setFont(undefined, "normal");
    doc.text("Company presentation/seminar", marginLeft + 15, referralTableY);
    doc.rect(marginLeft + 5, referralTableY - 4, 4, 4); // Checkbox

    // B: Marine Club noticeboard
    doc.setFont(undefined, "bold");
    doc.text("B", marginLeft + referralColWidth + 5, referralTableY);
    doc.setFont(undefined, "normal");
    doc.text("Marine Club noticeboard", marginLeft + referralColWidth + 15, referralTableY);
    doc.rect(marginLeft + referralColWidth + 5, referralTableY - 4, 4, 4); // Checkbox

    // C: Marine magazine advertisement
    doc.setFont(undefined, "bold");
    doc.text("C", marginLeft + (referralColWidth * 2) + 5, referralTableY);
    doc.setFont(undefined, "normal");
    doc.text("Marine magazine", marginLeft + (referralColWidth * 2) + 15, referralTableY);
    doc.text("advertisement", marginLeft + (referralColWidth * 2) + 15, referralTableY + 4);
    doc.rect(marginLeft + (referralColWidth * 2) + 5, referralTableY - 4, 4, 4); // Checkbox

    // Row 2
    // D: Newspaper advertisement
    doc.setFont(undefined, "bold");
    doc.text("D", marginLeft + 5, referralTableY + 16);
    doc.setFont(undefined, "normal");
    doc.text("Newspaper advertisement", marginLeft + 15, referralTableY + 16);
    doc.rect(marginLeft + 5, referralTableY + 12, 4, 4); // Checkbox

    // E: Told by seagoing friend(s)
    doc.setFont(undefined, "bold");
    doc.text("E", marginLeft + referralColWidth + 5, referralTableY + 16);
    doc.setFont(undefined, "normal");
    doc.text("Told by seagoing friend (s)", marginLeft + referralColWidth + 15, referralTableY + 16);
    doc.rect(marginLeft + referralColWidth + 5, referralTableY + 12, 4, 4); // Checkbox

    // F: Direct Mail from Company
    doc.setFont(undefined, "bold");
    doc.text("F", marginLeft + (referralColWidth * 2) + 5, referralTableY + 16);
    doc.setFont(undefined, "normal");
    doc.text("Direct Mail from Company", marginLeft + (referralColWidth * 2) + 15, referralTableY + 16);
    doc.rect(marginLeft + (referralColWidth * 2) + 5, referralTableY + 12, 4, 4); // Checkbox

    // Row 3
    // G: Others (Please specify)
    doc.setFont(undefined, "bold");
    doc.text("G", marginLeft + 5, referralTableY + 32);
    doc.setFont(undefined, "normal");
    doc.text("Others (Please specify):", marginLeft + 15, referralTableY + 32);
    doc.rect(marginLeft + 5, referralTableY + 28, 4, 4); // Checkbox
    doc.text("_____________________________", marginLeft + 60, referralTableY + 32);

    y = referralTableY + referralTableHeight + 10;

    // ==============================
    // SECTION 9: PERSONAL DETAILS
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text("9. PERSONAL DETAILS", marginLeft, y);
    y += lineHeight + 4;

    doc.setFontSize(9);

    // Height
    doc.setFont(undefined, "bold");
    doc.text("Height:", marginLeft, y);
    doc.setFont(undefined, "normal");
    const height = crew?.height || "______";
    doc.text(`${height} cm`, marginLeft + 20, y);

    // Weight
    doc.setFont(undefined, "bold");
    doc.text("Weight:", marginLeft + 70, y);
    doc.setFont(undefined, "normal");
    const weight = crew?.weight || "______";
    doc.text(`${weight} Kgs`, marginLeft + 90, y);

    y += lineHeight + 4;

    // Boiler Suits size
    doc.setFont(undefined, "bold");
    doc.text("Boiler Suits size:", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text("[ S, M, L, XL, XXL ]:", marginLeft + 40, y);

    // Create checkboxes for boiler suit sizes
    const boilerSuitSizes = ["S", "M", "L", "XL", "XXL"];
    let sizeX = marginLeft + 90;
    boilerSuitSizes.forEach((size, index) => {
      doc.text(size, sizeX + (index * 12), y);
      doc.rect(sizeX + (index * 12) - 3, y - 4, 3, 3);
    });

    y += lineHeight + 4;

    // Safety Shoes size
    doc.setFont(undefined, "bold");
    doc.text("Safety Shoes size:", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text("[ 6, 7, 8, 9, 10, 11 ]:", marginLeft + 40, y);

    // Create checkboxes for shoe sizes
    const shoeSizes = ["6", "7", "8", "9", "10", "11"];
    let shoeX = marginLeft + 95;
    shoeSizes.forEach((size, index) => {
      doc.text(size, shoeX + (index * 10), y);
      doc.rect(shoeX + (index * 10) - 3, y - 4, 3, 3);
    });

    y += lineHeight + 8;

    // Any major illness / Surgery
    doc.setFont(undefined, "bold");
    doc.text("Any major illness / Surgery:", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text("YES", marginLeft + 65, y);
    doc.rect(marginLeft + 60, y - 4, 4, 4); // Checkbox for YES
    doc.text("NO", marginLeft + 80, y);
    doc.rect(marginLeft + 75, y - 4, 4, 4); // Checkbox for NO

    y += lineHeight + 2;
    doc.setFont(undefined, "italic");
    doc.text('(If "YES", Kindly provide details separately)', marginLeft + 65, y);

    y += lineHeight + 8;

    // ==============================
    // SECTION 10: DECLARATION
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(10);
    doc.text("10. DECLARATION", marginLeft, y);
    y += lineHeight + 4;

    doc.setFontSize(9);
    doc.setFont(undefined, "normal");

    // Declaration text
    const declarationText = `I declare that the information given by me in filling up this form to the best of my knowledge are true complete. I also declare that the documents submitted to ${selectedAgentData?.companyName} are genuine, given and signed by the persons whose names appear on them. Any false declaration may render me liable for termination or dismissal.`;

    // Split text into multiple lines
    const splitDeclaration = doc.splitTextToSize(declarationText, contentWidth - 10);
    doc.text(splitDeclaration, marginLeft + 5, y);

    y += (splitDeclaration.length * lineHeight) + 8;

    // Date
    doc.setFont(undefined, "bold");
    doc.text("Date:", marginLeft, y);
    doc.setFont(undefined, "normal");
    const today = new Date();
    const formattedDate = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
    doc.text(formattedDate, marginLeft + 20, y);

    y += lineHeight + 4;

    // Signature
    doc.setFont(undefined, "bold");
    doc.text("SIGNATURE OF APPLICANT:", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text("_____________________________", marginLeft + 70, y);

    y += lineHeight + 2;

    // Applicant name below signature
    doc.setFont(undefined, "italic");
    doc.text(`(${crew?.name || "Applicant Name"})`, marginLeft + 70, y + 2);

    y += lineHeight + 10;



    // ==============================
    // FOOTER
    // ==============================
    doc.setFontSize(7);
    doc.setTextColor(100);
    doc.setFont(undefined, "normal");
    doc.text(
      "This document is electronically generated and does not require a physical signature.",
      pageWidth / 2,
      pageHeight - 8,
      { align: "center" }
    );

    // Add page number
    doc.setFontSize(8);
    doc.setTextColor(0);
    doc.text(`Page ${pageNumber}`, pageWidth - marginRight, pageHeight - 15, { align: "right" });

    doc.save("Application_Form_Page5.pdf");
  };

  const generateApplicationFormSix = async (crew, agentLogo, selectedAgentData) => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });


    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const marginLeft = 15;
    const marginRight = 15;
    const contentWidth = pageWidth - marginLeft - marginRight;
    const lineHeight = 6;

    let y = 20;
    let pageNumber = 1;

    // ==============================
    // HEADER SECTION
    // ==============================

    // Logo (Top Left) - taken from selectedAgentData
    if (selectedAgentData?.logo?.filePath) {
      try {
        // Clean up the file path - replace backslashes with forward slashes
        const cleanFilePath = selectedAgentData.logo.filePath.replace(/\\/g, '/');

        const logoUrl = `${baseURL}/${cleanFilePath}`;

        // Determine image format from file extension
        const fileExtension = selectedAgentData.logo.filePath.split('.').pop().toLowerCase();
        let imageFormat = 'JPEG'; // Default format

        // Map common extensions to jsPDF supported formats
        const formatMap = {
          'jpg': 'JPEG',
          'jpeg': 'JPEG',
          'png': 'PNG',
          'gif': 'GIF',
          'bmp': 'BMP',
          'webp': 'WEBP'
        };

        imageFormat = formatMap[fileExtension] || 'JPEG';

        doc.addImage(logoUrl, imageFormat, marginLeft, 10, 35, 18);
      } catch (err) {
        console.log("Logo error:", err);
      }
    }

    // Company Information (Top Right)
    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text(
      selectedAgentData?.companyName || "SOULITE SHIP MANAGEMENT PVT LTD",
      pageWidth - marginRight,
      12,
      { align: "right" }
    );

    doc.setFontSize(9);
    doc.setFont(undefined, "normal");

    // Address
    const address = selectedAgentData?.address || "D-22, First Floor, Office No-03, Sector-03, Noida (UP) 201301";
    doc.text(address, pageWidth - marginRight, 18, {
      align: "right",
    });

    // Email
    doc.text(
      `Email: ${selectedAgentData?.email || "hr@souliteshipmanagement.com"}`,
      pageWidth - marginRight,
      24,
      { align: "right" }
    );

    // Mobile Number
    doc.text(
      `Tel: ${selectedAgentData?.mobileNumber || "+917303611301"}`,
      pageWidth - marginRight,
      30,
      { align: "right" }
    );

    // Divider Line
    doc.line(marginLeft, 38, pageWidth - marginRight, 38);

    y = 48;
    // ==============================
    // SECTION 11: SPACE FOR OFFICIAL USE
    // ==============================
    doc.setFont(undefined, "bold");
    doc.setFontSize(12);
    doc.text("11. SPACE FOR OFFICIAL USE", marginLeft, y);
    y += lineHeight + 8;

    doc.setFontSize(10);

    // Assessment Table
    const assessmentTableY = y;
    const assessmentTableHeight = 80;

    // Draw outer border for assessment section
    doc.rect(marginLeft, assessmentTableY - 4, contentWidth, assessmentTableHeight);

    // Draw vertical line to separate Assessment from Grade
    doc.line(marginLeft + 90, assessmentTableY - 4, marginLeft + 90, assessmentTableY + assessmentTableHeight - 4);

    // Assessment header
    doc.setFont(undefined, "bold");
    doc.text("Assessment", marginLeft + 35, assessmentTableY);
    doc.text("Grade", marginLeft + 115, assessmentTableY);

    // Draw horizontal lines for each row
    const rowPositions = [
      assessmentTableY + 8,   // Personality
      assessmentTableY + 20,  // Attitude
      assessmentTableY + 32,  // Technical Knowledge
      assessmentTableY + 44,  // English Knowledge
      assessmentTableY + 56,  // Overall Assessment
      assessmentTableY + 68   // Grade Legend
    ];

    rowPositions.forEach((pos, index) => {
      if (index < rowPositions.length - 1) { // Don't draw line after last row
        doc.line(marginLeft, pos, pageWidth - marginRight, pos);
      }
    });

    // Assessment items - Left column
    doc.setFont(undefined, "normal");
    doc.text("Personality", marginLeft + 10, assessmentTableY + 6);

    // Assessment items - Right column (Grade)
    doc.text(":", marginLeft + 80, assessmentTableY + 6);
    doc.text("____________", marginLeft + 95, assessmentTableY + 6);

    doc.text("Attitude", marginLeft + 10, assessmentTableY + 18);
    doc.text(":", marginLeft + 80, assessmentTableY + 18);
    doc.text("____________", marginLeft + 95, assessmentTableY + 18);

    doc.text("Technical Knowledge", marginLeft + 10, assessmentTableY + 30);
    doc.text(":", marginLeft + 80, assessmentTableY + 30);
    doc.text("____________", marginLeft + 95, assessmentTableY + 30);

    doc.text("English Knowledge", marginLeft + 10, assessmentTableY + 42);
    doc.text(":", marginLeft + 80, assessmentTableY + 42);
    doc.text("____________", marginLeft + 95, assessmentTableY + 42);

    doc.text("Overall Assessment", marginLeft + 10, assessmentTableY + 54);
    doc.text(":", marginLeft + 80, assessmentTableY + 54);
    doc.text("____________", marginLeft + 95, assessmentTableY + 54);

    // Grade Legend
    doc.setFontSize(8);
    doc.text("Grade: A – Excellent, B – Good, C – Satisfactory, D – Average, E – Poor", marginLeft + 10, assessmentTableY + 68);

    y = assessmentTableY + assessmentTableHeight + 8;

    // ==============================
    // INTERVIEWER SECTION
    // ==============================
    doc.setFontSize(10);

    // Interviewed by
    doc.setFont(undefined, "bold");
    doc.text("Interviewed by", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text("________________________", marginLeft + 30, y);

    // Signature & Date
    doc.setFont(undefined, "bold");
    doc.text("Signature & Date:", marginLeft + 100, y);
    doc.setFont(undefined, "normal");
    doc.text("________________________", marginLeft + 130, y);

    y += lineHeight + 8;

    // INTERVIEWER'S REMARK (first)
    doc.setFont(undefined, "bold");
    doc.text("INTERVIEWER'S REMARK:", marginLeft, y);
    doc.setFont(undefined, "normal");

    // Split long remark line into multiple lines if needed
    const remarkLine = "____________________________________________________________";
    const remarkLines = doc.splitTextToSize(remarkLine, contentWidth - 60);
    doc.text(remarkLines[0], marginLeft + 45, y);

    y += lineHeight + 4;

    // Second line for remark (continuation)
    if (remarkLines.length > 1) {
      doc.text(remarkLines[1], marginLeft + 45, y);
    } else {
      doc.text(remarkLine, marginLeft + 45, y);
    }

    y += lineHeight + 8;

    // Second INTERVIEWER'S REMARK
    doc.setFont(undefined, "bold");
    doc.text("INTERVIEWER'S REMARK:", marginLeft, y);
    doc.setFont(undefined, "normal");
    doc.text(remarkLine, marginLeft + 45, y);

    y += lineHeight + 4;

    // Second line for second remark
    doc.text(remarkLine, marginLeft + 45, y);

    y += lineHeight + 15;

    // ==============================
    // ACKNOWLEDGEMENT SECTION
    // ==============================

    // Draw a light border around acknowledgement section
    doc.setDrawColor(200);
    doc.rect(marginLeft, y - 4, contentWidth, 30);
    doc.setDrawColor(0);

    doc.setFontSize(10);
    doc.setFont(undefined, "bold");
    doc.text("ACKNOWLEDGEMENT OF APPLICATION FOR RECRUITMENT OR PLACE", pageWidth / 2, y, {
      align: "center",
    });

    y += lineHeight + 4;

    doc.setFontSize(12);
    doc.setFont(undefined, "bold");
    doc.text("Annex \"A\"", pageWidth / 2, y, {
      align: "center",
    });

    y += lineHeight + 8;

    // ==============================
    // FOOTER
    // ==============================
    doc.setFontSize(7);
    doc.setTextColor(100);
    doc.setFont(undefined, "normal");
    doc.text(
      "This document is electronically generated and does not require a physical signature.",
      pageWidth / 2,
      pageHeight - 8,
      { align: "center" }
    );

    // Add page number
    doc.setFontSize(8);
    doc.setTextColor(0);
    doc.text(`Page ${pageNumber}`, pageWidth - marginRight, pageHeight - 15, { align: "right" });

    doc.save("Application_Form_Page6.pdf");
  };



  // ✅ Generate Handler
  const handleGenerate = async () => {
    if (!selectedCrew || !selectedAgent || !selectedForm) {
      alert("Please select all fields");
      return;
    }

    // 🔥 If Seafarer Declaration → Frontend PDF
    if (selectedForm === "Seafarer Declaration") {
      const selectedAgentData = agentList.find(
        (agent) => agent._id === selectedAgent
      );

      generateSeafarerDeclaration(
        crew,
        selectedAgentData?.logo?.base64 || null,
        selectedAgentData
      );
      return;
    }

    if (selectedForm === "Drug and Alcohol Policy") {
      const selectedAgentData = agentList.find(
        (agent) => agent._id === selectedAgent
      );

      generateDrugAlcohalpolicy(
        crew,
        selectedAgentData?.logo?.base64 || null,
        selectedAgentData
      );
      return;
    }

    if (selectedForm === "Marpol Compliance") {
      const selectedAgentData = agentList.find(
        (agent) => agent._id === selectedAgent
      );

      generateMarpolCompliance(
        crew,
        selectedAgentData?.logo?.base64 || null,
        selectedAgentData
      );
      return;
    }

    if (selectedForm === "Next of Kin Declaration") {
      const selectedAgentData = agentList.find(
        (agent) => agent._id === selectedAgent
      );

      generateNextofKinDeclaration(
        crew,
        selectedAgentData?.logo?.base64 || null,
        selectedAgentData
      );
      return;
    }

    if (selectedForm === "Seafarer Awareness Form") {
      const selectedAgentData = agentList.find(
        (agent) => agent._id === selectedAgent
      );

      generateSeafarerAwarenessform(
        crew,
        selectedAgentData?.logo?.base64 || null,
        selectedAgentData
      );
      return;
    }

    if (selectedForm === "Acknowledgement of application") {
      const selectedAgentData = agentList.find(
        (agent) => agent._id === selectedAgent
      );

      generateAcknowledgementofapplication(
        crew,
        selectedAgentData?.logo?.base64 || null,
        selectedAgentData
      );
      return;
    }

    if (selectedForm === "Application Form") {
      const selectedAgentData = agentList.find(
        (agent) => agent._id === selectedAgent
      );

      generateApplicationFormone(
        crew,
        selectedAgentData?.logo?.base64 || null,
        selectedAgentData
      );
      return;
    }

    if (selectedForm === "Application Form - 2") {
      const selectedAgentData = agentList.find(
        (agent) => agent._id === selectedAgent
      );

      generateApplicationFormTwo(
        crew,
        selectedAgentData?.logo?.base64 || null,
        selectedAgentData
      );
      return;
    }

    if (selectedForm === "Application Form - 3") {
      const selectedAgentData = agentList.find(
        (agent) => agent._id === selectedAgent
      );

      generateApplicationFormThree(
        crew,
        selectedAgentData?.logo?.base64 || null,
        selectedAgentData
      );
      return;
    }

    if (selectedForm === "Application Form - 4") {
      const selectedAgentData = agentList.find(
        (agent) => agent._id === selectedAgent
      );

      generateApplicationFormFour(
        crew,
        selectedAgentData?.logo?.base64 || null,
        selectedAgentData
      );
      return;
    }

    if (selectedForm === "Application Form - 5") {
      const selectedAgentData = agentList.find(
        (agent) => agent._id === selectedAgent
      );

      generateApplicationFormFive(
        crew,
        selectedAgentData?.logo?.base64 || null,
        selectedAgentData
      );
      return;
    }

    if (selectedForm === "Application Form - 6") {
      const selectedAgentData = agentList.find(
        (agent) => agent._id === selectedAgent
      );

      generateApplicationFormSix(
        crew,
        selectedAgentData?.logo?.base64 || null,
        selectedAgentData
      );
      return;
    }

    // 🔥 Other Forms → Backend Call
    try {
      setLoading(true);

      await axios.post(`${baseURL}/api/prejoining/generate`, {
        crewId: selectedCrew,
        agentId: selectedAgent,
        formType: selectedForm,
      });

      setSuccessOpen(true);
    } catch (error) {
      console.log(error);
      alert("Generation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 5 }}>
      <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Pre-Joining Form Generator
        </Typography>

        <Box display="flex" flexDirection="column" gap={3} mt={3}>
          {/* Crew */}
          <FormControl fullWidth>
            <InputLabel>Crew</InputLabel>
            <Select value={selectedCrew} disabled>
              {crew && (
                <MenuItem value={crew._id}>{crew.name}</MenuItem>
              )}
            </Select>
          </FormControl>

          {/* Agent */}
          <FormControl fullWidth>
            <InputLabel>Select Crewing Agent</InputLabel>
            <Select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
            >
              {agentList.map((agent) => (
                <MenuItem key={agent._id} value={agent._id}>
                  {agent.companyName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Form Type */}
          <FormControl fullWidth>
            <InputLabel>Select Form Type</InputLabel>
            <Select
              value={selectedForm}
              onChange={(e) => setSelectedForm(e.target.value)}
            >
              <MenuItem value="Seafarer Declaration">
                Seafarer Declaration
              </MenuItem>
              <MenuItem value="Seafarer Awareness Form">
                Seafarer Awareness Form
              </MenuItem>
              <MenuItem value="Next of Kin Declaration">
                Next of Kin Declaration
              </MenuItem>
              <MenuItem value="Application Form - 6">
                Application Form - 6
              </MenuItem>
              <MenuItem value="Application Form - 5">
                Application Form - 5
              </MenuItem>
              <MenuItem value="Application Form - 4">
                Application Form - 4
              </MenuItem>
              <MenuItem value="Application Form - 3">
                Application Form - 3
              </MenuItem>
              <MenuItem value="Application Form - 2">
                Application Form - 2
              </MenuItem>
              <MenuItem value="Application Form">
                Application Form
              </MenuItem>
              <MenuItem value="Drug and Alcohol Policy">
                Drug and Alcohol Policy
              </MenuItem>
              <MenuItem value="Marpol Compliance">
                Marpol Compliance
              </MenuItem>
              <MenuItem value="Acknowledgement of application">
                Acknowledgement of application
              </MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            size="large"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Generate Form"}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={successOpen}
        autoHideDuration={3000}
        onClose={() => setSuccessOpen(false)}
      >
        <Alert severity="success" variant="filled">
          Form Generated Successfully!
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Prejoining;